/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { React, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
//   utils
import { fData } from 'src/utils/format-number';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { UploadBox } from 'src/components/upload';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { _tags } from 'src/_mock';
import FormProvider, {
  RHFTextField,
  RHFAutocomplete,
  RHFRadioGroup,
  RHFUploadBox,
  RHFUploadAvatar,
} from 'src/components/hook-form';
import {
  Alert,
  Backdrop,
  Chip,
  CircularProgress,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useBoolean } from 'src/hooks/use-boolean';
import { ConfirmDialog } from 'src/components/custom-dialog';
import axiosInstance from 'src/api/axiosInstance';
import { getCurrentProject, getCurrentServer, getMandEndPoint } from 'src/helper/server-address';
import ListboxComponent from 'src/components/virtualized-autocomplete/autocomplete-virtualized';
import { blockInvalidChar } from 'src/helper/blockInvalidCharNumFld';
import useNavigateErrorHandler from 'src/hooks/useNavigateErrorHandler';
import LinearAlternativeLabel from './linear-alternative-label-stepper';
import CandNewEditEduCompo from './cand-newEdit-edu-compo';
import CandNewEditExpCompo from './cand-newEdit-exp-compo';
import CandNewEditCertiCompo from './cand-newEdit-certi-compo';
import CandNewEditRefCompo from './cand-newEdit-ref-compo';
// eslint-disable-next-line import/order
import { fetchEncryptedToken } from 'src/utils/encryptUtils';

export default function CandidateNewEditForm({
  currentCand,
  currentCandPrefLoc,
  currentCandEduDtl,
  currentCandSkils,
  currentCandCertDtl,
  currentCandExpDtl,
  currentCandRefDtl,
  currentCandView,
  disabled,
}) {
  const token = sessionStorage.getItem('token');
  const loadingSend = useBoolean();
  const quickAutoFill = useBoolean();
  const confirm = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  const [rFileNm, setRFileNm] = useState('');
  const [canid, setCanid] = useState();
  const [cPrefLoc, setCPrefLoc] = useState([]);
  const [cEduDtl, setCEduDtl] = useState([]);
  const [cPskl, setCPSkl] = useState([]);
  const [cSskl, setCSSkl] = useState([]);
  const [cExpDtl, setCExpDtl] = useState([]);
  const [cRefDtl, setCRefDtl] = useState([]);
  const [cCertDtl, setCCertDtl] = useState([]);
  const [candEmailArr, setCandEmailArr] = useState([]);
  const [candUniqNoArr, setCandUniqNoArr] = useState([]);
  const [editResume, setEditResume] = useState(false);
  const [resumeTyp, setResumeTyp] = useState('');
  const [fileToAutoFill, setFileToAutoFill] = useState(null);
  const [isResumeProcessing, setIsResumeProcessing] = useState(false);
  const router = useRouter();
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));
  const [curCandPic, setCurCandPic] = useState(false);
  const [isProfilePhotoRemoved, setIsProfilePhotoRemoved] = useState(false);
  const handleApiError = useNavigateErrorHandler();
  const [isFileTooLarge, setIsFileTooLarge] = useState(false);

  // -------------------------------------------------------------------------------
  // stepper Form Components
  const steps = [
    'Basic Details',
    'Personal Details',
    'Professional Details',
    'Education Details',
    'Certification Details',
    'Experience Details',
    'Reference Details',
  ];

  const stepsvalidator = [
    { label: 'Basic Details', fields: { resumeUpload: '', srcofhire: '' } },
    {
      label: 'Personal Details',
      fields: { fname: '', lname: '', email: '', mobno: '', gender: '', cuurntloc: '' },
    },
    { label: 'Professional Details', fields: { primaryskl: '', candQal: '' } },
    { label: 'Education Details', fields: { candEdu: '' } },
    { label: 'Certification Details', fields: { candCert: '' } },
    { label: 'Experience Details', fields: { candExp: '' } },
    { label: 'Reference Details', fields: { candRef: '' } },
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState();
  const [stepCompletionStatus, setStepCompletionStatus] = useState(
    new Array(steps.length).fill(null) // Use `null` instead of `false`
  );
  const [progress, Setprogress] = useState(0);
  const [isFinalStep, setIsFinalStep] = useState(false);

  const [switchState, setSwitchState] = useState({
    experience: false,
  });

  const aadhaarSchema = Yup.string()
    .matches(
      /^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/,
      'Aadhaar number is not valid (e.g., 234512341234)'
    )
    .nullable(); // Optional validation for Aadhar format

  const panSchema = Yup.string()
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'PAN card number is not valid (e.g., ABCDE1234F)')
    .nullable();

  const baseSchema = Yup.object().shape({
    ...(activeStep >= 1 && {
      fname: Yup.string()
        .required('First name is required. For example: Rajesh')
        .max(100, 'First name must not exceed 100 characters. For example: Rajesh or Priya')
        .test(
          'no-spaces',
          'First name cannot be only blank spaces or start/end with blank spaces. For example: "Rajesh" is valid, but "  Rajesh  " is not.',
          (value) => value.trim() !== '' && value === value.trim()
        )
        .matches(
          /^[a-zA-Z\s]*$/,
          'First name cannot contain numbers or special characters. For example: "Rajesh" is valid, but "Rajesh123" or "Rajesh@#" is not.'
        ),

      mname: Yup.string()
        .max(100, 'Middle name must not exceed 100 characters. For example: Kumar')
        .test(
          'no-spaces-mid',
          'Middle name cannot be only blank spaces or start/end with blank spaces. For example: "Kumar" is valid, but "  Kumar  " is not.',
          (value) => {
            if (!value) return true;
            return value.trim() !== '' && value === value.trim();
          }
        )
        .matches(
          /^[a-zA-Z\s]*$/,
          'Middle name cannot contain numbers or special characters. For example: "Kumar" is valid, but "Kumar123" or "Kumar@#" is not.'
        ),

      lname: Yup.string()
        .required('Last name is required. For example: Gupta')
        .max(100, 'Last name must not exceed 100 characters. For example: Gupta or Sharma')
        .test(
          'no-spaces',
          'Last name cannot be only blank spaces or start/end with blank spaces. For example: "Gupta" is valid, but "  Gupta  " is not.',
          (value) => value.trim() !== '' && value === value.trim()
        )
        .matches(
          /^[a-zA-Z\s]*$/,
          'Last name cannot contain numbers or special characters. For example: "Gupta" is valid, but "Gupta123" or "Gupta@#" is not.'
        ),

      mobno: Yup.string()
        .required('Mobile number is required. For example: 9876543210')
        .max(12, 'Enter a valid mobile number. For example: 9876543210')
        .min(10, 'Enter a valid mobile number. For example: 9876543210')
        .matches(
          /^[0-9]+$/,
          'Mobile number cannot contain + or - signs. For example: "9876543210" is valid, but "+919876543210" is not.'
        )
        .test(
          'phncode-required',
          'Country Code is required. For example: "+91" for India',
          (value, context) => {
            const { phncode } = context.parent;
            if (!phncode) {
              return false;
            }
            return true;
          }
        ),
      gender: Yup.object().required('Gender is required'),
      cuurntloc: Yup.object().required('Current location is required'),

      srcofhire: Yup.object().required('Source of hire is required'),
      srcofhireNm: Yup.string()
        .max(100, 'Source name must not exceed 100 characters')
        .test(
          'no-spaces-mid',
          'Source name cannot be only blank spaces or start/end with blank spaces (e.g., " Naukri " is wrong)',
          (value) => {
            if (!value) return true;
            return value.trim() !== '' && value === value.trim();
          }
        )
        .matches(
          /^[a-zA-Z\s]*$/,
          'Source name cannot contain numbers or special characters (e.g., "Naukri23", "@#TimesJobs" are wrong)'
        ),

      currentcomp: Yup.string()
        .notRequired()
        .max(100, 'Current Employer must not exceed 100 characters')
        .test(
          'no-spaces-mid',
          'Current Employer cannot be only blank spaces or start/end with blank spaces (e.g., "  Infosys")',
          (value) => {
            if (!value) return true;
            return value.trim() !== '' && value === value.trim();
          }
        ),

      linkedinUrl: Yup.string()
        .notRequired()
        .test(
          'is-valid-url',
          'Enter a valid LinkedIn profile URL (e.g., "https://www.linkedin.com/in/firstname-lastname/")',
          (value) => {
            if (!value) return true;
            const regex =
              /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/gi;
            return regex.test(value) && value.includes('linkedin.com');
          }
        ),
    }),
    // if activeStep ==2 then only below fields Validation will triggered it changed from Sprint 6 from === to >=
    ...(activeStep >= 2 && {
      primaryskl: Yup.array()
        .required('Please select primary skill')
        .min(1, 'Please select primary skill'),
      candQal: Yup.object().required('Highest qualification is required'),
    }),
    // resumeUpload: Yup.mixed().when(currentCand !== null).required('Resume is required'),
    ...(activeStep >= 5 && {
      candExp: Yup.array()
        .of(
          Yup.object().shape({
            compNm: Yup.string()
              .required('Employer name is required (e.g., "Infosys", "Wipro")')
              .max(100, 'Employer name must not exceed 100 characters')
              .test(
                'no-spaces',
                'Employer name cannot be only blank spaces or start/end with blank spaces (e.g., "  Infosys")',
                (value) => value.trim() !== '' && value === value.trim()
              ),

            designation: Yup.object().required('Designation is required'),
            ctc: Yup.number()
              .test(
                'noEOrSign', // type of the validator (should be unique)
                "Number had an 'e' or sign.", // error message
                (value) => {
                  if (!value) return true;
                  return /^[0-9]+$/.test(value.toString());
                }
              )
              .min(0, 'CTC cannot be in negetive')
              .max(2000000000, 'CTC cannot be more then 2000000000')
              .test('phncode-required', 'Currency is required', (value, context) => {
                // Arrow function
                const { ctccurncy } = context.parent; // Extract phncode from context.parent
                if (value && !ctccurncy) {
                  // If only country code is missing
                  return false;
                }
                return true; // If country code is valid
              }),
            expfromDate: Yup.date()
              .required('From date is required')
              .typeError('From date must be a valid date')
              .test('no-future-date', 'From date cannot be a future date', (value, context) => {
                if (!value) return true;
                return new Date(value).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0);
              })
              .test('less-than-to-date', 'From date must be less than To date', (value, context) => {
                if (!value) return true;
                const toDate = context.parent.exptoDate;
                if (!toDate) return true;
                return new Date(value).setHours(0, 0, 0, 0) < new Date(toDate).setHours(0, 0, 0, 0);
              }),
            exptoDate: Yup.date()
              .nullable()
              .typeError('To date must be a valid date')
              .test('greater-than-from-date', 'To date must be greater than From date', (value, context) => {
                if (!value) return true;
                const fromDate = context.parent.expfromDate;
                if (!fromDate) return true;
                return new Date(value).setHours(0, 0, 0, 0) > new Date(fromDate).setHours(0, 0, 0, 0);
              })
              .test('min-date-one-month', 'To Date should be at least 1 month after From Date', (value, context) => {
                if (!value) return true;
                const fromDate = context.parent.expfromDate;
                if (!fromDate) return true;
                const from = new Date(fromDate);
                const to = new Date(value);
                const oneMonthLater = new Date(from);
                oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
                return to.setHours(0, 0, 0, 0) >= oneMonthLater.setHours(0, 0, 0, 0);
              })
              .when('currentywrkng', {
                is: (val) => val === true,
                then: (schema) =>
                  schema
                    .notRequired()
                    .test('max-current-plus-3-months', 'To date cannot be more than 3 months in the future', (value) => {
                      if (!value) return true;
                      const maxAllowedDate = new Date();
                      maxAllowedDate.setMonth(maxAllowedDate.getMonth() + 3);
                      return new Date(value).setHours(0, 0, 0, 0) <= maxAllowedDate.setHours(0, 0, 0, 0);
                    }),
                otherwise: (schema) =>
                  schema
                    .required('To date is required')
                    .test('max-today', 'To date cannot be in the future', (value) => {
                      if (!value) return true;
                      return new Date(value).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0);
                    }),
              }),
          })
        )
        .test(
          'unique-employer-designation',
          null, // We won't provide a default error message here
          (value, context) => {
            if (!value) return true; // If the array is empty or undefined, consider it valid

            const seen = new Set();
            const errors = [];

            value.forEach((entry, index) => {
              const company = entry.compNm;
              const desgn = entry.designation?.designationName;

              if (!company || !desgn) return;

              const identifier = `${company}-${desgn}`;

              if (seen.has(identifier)) {
                errors.push(index); // Track the index of the duplicate entry
              } else {
                seen.add(identifier);
              }
            });

            if (errors.length) {
              return context.createError({
                path: `candExp[${errors[0]}].compNm`, // Path to the field with the duplicate
                message:
                  'You have already selected this employer and designation in your experience details.',
              });
            }

            return true;
          }
        )
        .test('no-overlapping-dates', function (expArray) {
          if (!expArray || expArray.length < 2) return true;

          const { createError, path } = this;

          const ranges = expArray
            .map((exp, idx) => {
              const from = exp.expfromDate ? new Date(exp.expfromDate).getTime() : null;
              let to = exp.exptoDate ? new Date(exp.exptoDate).getTime() : null;
              if (exp.currentywrkng && !to) {
                to = Infinity;
              }
              if (from === null || to === null) return null;
              return { idx, from, to };
            })
            .filter(Boolean);

          for (let i = 0; i < ranges.length; i += 1) {
            for (let j = i + 1; j < ranges.length; j += 1) {
              const r1 = ranges[i];
              const r2 = ranges[j];

              if (r1.from <= r2.to && r2.from <= r1.to) {
                // Attach error to the first overlapping experience's from date path
                return createError({
                  path: `candExp[${r1.idx}].expfromDate`,
                  message: 'This experience overlaps with another experience date range.',
                });
              }
            }
          }

          return true; // No overlaps
        }),
    }),
    ...(activeStep >= 3 && {
      candEdu: Yup.array()
        .of(
          Yup.object().shape({
            university: Yup.object().required('University is required'),
            qualtype: Yup.object().required('Qualification type is required'),
            qualification: Yup.object().required('Qualification is required'),
            stream: Yup.object().required('Qualification Stream is required'),
            candpercnt: Yup.number()
              .typeError('Percentage must be a valid number')
              .min(0, 'Percentage cannot be less than 0%')
              .max(100, 'Percentage cannot be greater than 100%')
              .required('Percentage is required') // Make sure the field is required
              .test(
                'at-least-one',
                'Either percentage or CGPA is required',
                (value, context) => value !== 0.0 || context.parent.candcgpa !== 0.0
              ),
            candcgpa: Yup.number()
              .min(0, 'CGPA cannot be less than 0')
              .max(10, 'CGPA cannot be greater than 10')
              .test(
                'at-least-one',
                'Either percentage or CGPA is required',
                (value, context) => value !== 0.0 || context.parent.candpercnt !== 0.0
              ),
            edufromDate: Yup.date()
              .required('From date is required')
              .typeError('From date must be a valid date'),

            edutoDate: Yup.date()
              .typeError('To date must be a valid date') // Ensures it's a valid date
              .when('currentypurs', {
                is: true, // If "currently pursuing" is true
                then: () => Yup.date().notRequired(), // Skip validation when pursuing
                otherwise: () =>
                  Yup.date()
                    .required('To date is required') // Make "To Date" required when not pursuing
                    .min(Yup.ref('edufromDate'), '"To Date" should be after "From Date"') // Ensure "To Date" is after "From Date"
                    .test(
                      'min-date-one-year',
                      '"To Date" should be at least 1 year greater than "From Date"',
                      function (value) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        const fromDate = this.parent.edufromDate; // Get "edufromDate" from parent context

                        // If either From Date or To Date is missing, return true (skip validation)
                        if (!fromDate || !value) return true;

                        // Convert to Date objects
                        const from = new Date(fromDate);
                        const to = new Date(value);

                        // Validate that To Date is at least 1 year after From Date
                        const oneYearLater = new Date(from.setFullYear(from.getFullYear() + 1));

                        return to >= oneYearLater; // Ensure To Date is at least 1 year greater than From Date
                      }
                    ),
              }),
          })
        )
        .test(
          'unique-qualification-stream',
          null, // We won't provide a default error message here
          (value, context) => {
            if (!value) return true; // If the array is empty or undefined, consider it valid

            const seen = new Set();
            const errors = [];

            value.forEach((entry, index) => {
              const qualificationTitle = entry.qualification?.qualificationTitle;
              const streamTitle = entry.stream?.streamTitle;

              if (!qualificationTitle || !streamTitle) return;

              const identifier = `${qualificationTitle}-${streamTitle}`;

              if (seen.has(identifier)) {
                errors.push(index); // Track the index of the duplicate entry
              } else {
                seen.add(identifier);
              }
            });

            if (errors.length) {
              // Return false to indicate that validation failed
              return context.createError({
                path: `candEdu[${errors[0]}].qualification`, // Point to the first duplicate found
                message:
                  'You have already selected this qualification and stream in your education details.',
              });
            }

            return true;
          }
        ),
    }),
    ...(activeStep >= 4 && {
      candCert: Yup.array()
        .of(
          Yup.object().shape({
            certiNm: Yup.string()
              .required('Certification name is required')
              .max(100, 'Certification name must not exceed 100 characters')
              .test(
                'no-spaces',
                'Certification name cannot be only blank spaces or start/end with blank spaces',
                (value) => value.trim() !== '' && value === value.trim()
              ),
            certiInstiNm: Yup.string()
              .max(100, 'Certificate institute name must not exceed 100 characters')
              .test(
                'no-spaces-mid',
                'Certificate institute name cannot be only blank spaces or start/end with blank spaces',
                (value) => {
                  if (!value) return true;
                  return value.trim() !== '' && value === value.trim();
                }
              ),
            certfromDate: Yup.date().nullable().typeError('From date must be a valid date'),
            certtoDate: Yup.date()
              .nullable()
              .typeError('To date must be a valid date')
              .min(Yup.ref('certfromDate'), '"To Date" should be greater than "From Date"'),
          })
        )
        .test(
          'unique-certification',
          null, // We won't provide a default error message here
          (value, context) => {
            if (!value) return true; // If the array is empty or undefined, consider it valid

            const seen = new Set();
            const errors = [];

            value.forEach((entry, index) => {
              const certTitle = entry.certiNm;
              const instituteTitle = entry.certiInstiNm;

              if (!certTitle || !instituteTitle) return;

              const identifier = `${certTitle}-${instituteTitle}`;

              if (seen.has(identifier)) {
                errors.push(index); // Track the index of the duplicate entry
              } else {
                seen.add(identifier);
              }
            });

            if (errors.length) {
              // Return false to indicate that validation failed
              return context.createError({
                path: `candCert[${errors[0]}].certiNm`, // Point to the first duplicate found
                message:
                  'You have already selected this certification and institute in your certification details.',
              });
            }

            return true;
          }
        ),
    }),
    ...(activeStep >= 6 && {
      candRef: Yup.array()
        .of(
          Yup.object().shape({
            refNm: Yup.string()
              .required('Reference name is required (e.g., "Rajesh Sahani")')
              .max(100, 'Reference name must not exceed 100 characters')
              .test(
                'no-spaces',
                'Reference name cannot be only blank spaces or start/end with blank spaces (e.g., "  Rajesh Sahani")',
                (value) => value.trim() !== '' && value === value.trim()
              )
              .matches(
                /^[^\d]*$/,
                'Reference name cannot contain numbers (e.g., "123Rajesh Sahani" is wrong)'
              ),
            refOrgNm: Yup.string()
              .required('Reference employer is required (e.g., "TCS", "Infosys")')
              .max(
                100,
                'Reference employer must not exceed 100 characters (e.g., "HCL Technologies")'
              )
              .test(
                'no-spaces',
                'Reference employer cannot be only blank spaces or start/end with blank spaces (e.g., " Wipro")',
                (value) => value.trim() !== '' && value === value.trim()
              ),

            refdesig: Yup.object().required(
              'Reference designation is required (e.g., "Software Engineer", "Manager")'
            ),

            refEmail: Yup.string()
              .required('Reference email is required (e.g., "example@gmail.com")')
              .max(
                100,
                'Reference email must not exceed 100 characters (e.g., "firstname.lastname@company.com")'
              )
              .matches(
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                'Email must be a valid email address (e.g., "john.doe@gmail.com")'
              ),
            refContact: Yup.string().test(
              'is-valid-length',
              'Enter valid mobile number',
              (value) => {
                if (!value) return true; // If the field is empty, don't validate
                return value.length >= 10 && value.length <= 12;
              }
            ),
          })
        )
        .test(
          'unique-reference',
          null, // We won't provide a default error message here
          (value, context) => {
            if (!value) return true; // If the array is empty or undefined, consider it valid

            const seen = new Set();
            const errors = [];

            value.forEach((entry, index) => {
              const refNm = entry.refNm;
              const refOrg = entry.refOrgNm;

              if (!refNm || !refOrg) return;

              const identifier = `${refNm}-${refOrg}`;

              if (seen.has(identifier)) {
                errors.push(index); // Track the index of the duplicate entry
              } else {
                seen.add(identifier);
              }
            });

            if (errors.length) {
              // Return false to indicate that validation failed
              return context.createError({
                path: `candRef[${errors[0]}].refNm`, // Point to the first duplicate found
                message: 'You have already added this reference in your reference details.',
              });
            }

            return true;
          }
        ),
      // not required
      expinyrs: Yup.number()
        .max(100, 'Experience cannot more than 100 years')
        .min(0, 'Experience cannot be in negetive'),
      noticeprd: Yup.number()
        .max(365, 'Notice period cannot be more than 365 days.')
        .min(0, 'Notice period cannot be in negetive'),
      currentsal: Yup.number()
        .max(2000000000, 'Current salary cannot be more then 2000000000')
        .min(0, 'Current salary cannot be in negetive')
        .test('phncode-required', 'Currency is required', (value, context) => {
          // Arrow function
          const { currentsalcurncy } = context.parent; // Extract phncode from context.parent
          if (value && !currentsalcurncy) {
            // If only country code is missing
            return false;
          }
          return true; // If country code is valid
        }),
      exptsal: Yup.number()
        .max(2000000000, 'Expected salary cannot be more then 2000000000')
        .min(0, 'Expected salary cannot be in negetive')
        .test('phncode-required', 'Currency is required', (value, context) => {
          // Arrow function
          const { exptsalcurncy } = context.parent; // Extract phncode from context.parent
          if (value && !exptsalcurncy) {
            // If only country code is missing
            return false;
          }
          return true; // If country code is valid
        }),
      relexp: Yup.number()
        .max(
          Yup.ref('expinyrs'),
          '"Relevant Expeience" should be less than or equal to "Expericence"'
        )
        .min(0, 'Experience cannot be in negetive'),
      resigneddate: Yup.date().nullable().typeError('Resigned date must be a valid date'),
      canjoinby: Yup.date()
        .nullable()
        .typeError('Join by date must be a valid date')
        .min(Yup.ref('resigneddate'), '"Joining Date" should be greater than "Resigned Date"'),
    }),
  });

  const dynamicSchema = currentCand
    ? baseSchema.concat(
      Yup.object().shape({
        email: Yup.string()
          .required('Email is required')
          .max(100, 'Email must not exceed 100 characters')
          .matches(
            /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/,
            'Email must be a valid format (e.g., user@example.com)'
          )
          .test('unique', 'Email already exists', async (value) => {
            if (currentCand?.email === value) {
              return true; // Email hasn't changed, so it's valid
            }
            const filteredEmails = candEmailArr.filter((email) => email !== currentCand?.email);
            return !filteredEmails.includes(value); // Check for duplicates
          }),

        uniqueNum: Yup.string(),

        uniqueno: Yup.string().when('uniqueNum', ([uniqueNum], schema) => {
          if (uniqueNum === 'AADHAR') {
            return (
              Yup.string()
                .required('Aadhaar number is required')
                .matches(
                  /^[2-9]{1}[0-9]{11}$/,
                  'Invalid Aadhaar number. It must be a 12-digit number starting with 2-9. Example: 234567890123'
                )
                // eslint-disable-next-line prefer-arrow-callback
                .test('uniqueNo', 'Candidate already exists', async function (value) {
                  const filtered = candUniqNoArr.filter((num) => num !== currentCand?.panNo);
                  return !filtered.includes(value);
                })
            );
          }

          if (uniqueNum === 'PAN') {
            return (
              Yup.string()
                .required('PAN number is required')
                .matches(
                  /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                  'Invalid PAN number format. It must be 10 characters like ABCDE1234F'
                )
                // eslint-disable-next-line prefer-arrow-callback
                .test('uniqueNo', 'Candidate already exists', async function (value) {
                  const filtered = candUniqNoArr.filter((num) => num !== currentCand?.panNo);
                  return !filtered.includes(value);
                })
            );
          }

          return schema.notRequired(); // if neither selected, don't validate
        }),
      })
    )
    : baseSchema.concat(
      Yup.object().shape({
        resumeUpload: Yup.mixed().required('Resume is required'),
        email: Yup.string()
          .required('Email is required')
          .max(100, 'Email must not exceed 100 characters')
          .matches(
            /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/,
            'Email must be a valid format (e.g., user@example.com)'
          )
          .test(
            'unique',
            'Email already exists',
            async (value) => !candEmailArr.includes(value) // For new candidates
          ),
        uniqueno: Yup.string().when('uniqueNum', ([uniqueNum], schema) => {
          if (uniqueNum === 'AADHAR') {
            return (
              Yup.string()
                .required('Aadhaar number is required')
                .matches(
                  /^[2-9]{1}[0-9]{11}$/,
                  'Invalid Aadhaar number. It must be a 12-digit number starting with 2-9. Example: 234567890123'
                )
                // eslint-disable-next-line prefer-arrow-callback
                .test('uniqueNo', 'Candidate already exists', async function (value) {
                  const filtered = candUniqNoArr.filter((num) => num !== currentCand?.panNo);
                  return !filtered.includes(value);
                })
            );
          }

          if (uniqueNum === 'PAN') {
            return (
              Yup.string()
                .required('PAN number is required')
                .matches(
                  /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                  'Invalid PAN number format. It must be 10 characters like ABCDE1234F'
                )
                // eslint-disable-next-line prefer-arrow-callback
                .test('uniqueNo', 'Candidate already exists', async function (value) {
                  const filtered = candUniqNoArr.filter((num) => num !== currentCand?.panNo);
                  return !filtered.includes(value);
                })
            );
          }

          return schema.notRequired(); // if neither selected, don't validate
        }),
      })
    );

  const NewCandidateSchema = dynamicSchema;

  const getPrefLoc = useCallback(() => {
    if (currentCandPrefLoc) {
      const p = currentCandPrefLoc.map((pLoc) => pLoc.prefLocCity);
      setCPrefLoc(p);
    }
  }, [currentCandPrefLoc]);

  const toggleContent = useCallback(() => {
    if (currentCand) {
      setEditResume(true); // Toggle the boolean state
      setResumeTyp(currentCand.resume.split('.').pop().toLowerCase());
    }
  }, [currentCand]);

  const getSkl = useCallback(() => {
    if (currentCandSkils) {
      const ps = currentCandSkils.filter((item) => item.skillType === 'Primary');
      const prmS = ps.map((p) => p.skill);
      setCPSkl(prmS);
      const ss = currentCandSkils.filter((item) => item.skillType === 'Secondary');
      const secS = ss.map((p) => p.skill);
      setCSSkl(secS);
      setSecSkillArr(secS);
    }
  }, [currentCandSkils]);

  const getEduDtl = useCallback(() => {
    if (currentCandEduDtl) {
      const e = currentCandEduDtl.map((cEdu) => ({
        eduSrno: cEdu.candidateEducationDetailsId,
        university: cEdu.university,
        othrUni: cEdu.universityName,
        qualtype: cEdu.qualificationTypeId,
        qualification: cEdu.qualification,
        stream: cEdu.stream,
        candpercnt: cEdu.candidateQualPercentage,
        candcgpa: cEdu.candidateQualCgpa,
        currentypurs: cEdu.currentlyPursuing,
        edufromDate: cEdu.candidateQualFromDate ? new Date(cEdu.candidateQualFromDate) : null,
        edutoDate: cEdu.candidateQualToDate ? new Date(cEdu.candidateQualToDate) : null,
      }));
      setCEduDtl(e);
    }
  }, [currentCandEduDtl]);

  const getExpDtl = useCallback(() => {
    if (currentCandExpDtl) {
      const e = currentCandExpDtl.map((cExp) => ({
        expSrno: cExp.candidateExperienceDetailsId,
        compNm: cExp.candidateExperienceCompanyName,
        designation: cExp.candidateDesignation,
        ctc: cExp.candidateCtc,
        ctccurncy: cExp.candidateCtcCurrency,
        country: cExp.candidateExperienceCountryid,
        currentywrkng: cExp.candidateCurrentlyWorking,
        expfromDate: cExp.candidateExperienceFromDate
          ? new Date(cExp.candidateExperienceFromDate)
          : null,
        exptoDate: cExp.candidateExperienceToDate ? new Date(cExp.candidateExperienceToDate) : null,
      }));
      setCExpDtl(e);
    }
    // added the condition for experience switch
    if (currentCandExpDtl) {
      if (currentCandExpDtl.length > 0) {
        setSwitchState((prevState) => ({
          ...prevState,
          experience: true, // Set experience to true if length > 0
        }));
      } else {
        setSwitchState((prevState) => ({
          ...prevState,
          experience: false, // Set experience to false if length is 0
        }));
      }
    }
  }, [currentCandExpDtl]);

  const getCertDtl = useCallback(() => {
    if (currentCandCertDtl) {
      const e = currentCandCertDtl.map((cCrt) => ({
        cerSrno: cCrt.candidateCertificationId,
        certiNm: cCrt.certificationName,
        certiInstiNm: cCrt.instituteName,
        certfromDate: cCrt.durationFrom ? new Date(cCrt.durationFrom) : null,
        certtoDate: cCrt.durationTo ? new Date(cCrt.durationTo) : null,
      }));
      setCCertDtl(e);
    }
  }, [currentCandCertDtl]);

  const getRefDtl = useCallback(() => {
    if (currentCandRefDtl) {
      const e = currentCandRefDtl.map((cRef) => ({
        refSrno: cRef.candidateReferenceId,
        refNm: cRef.candidateReferenceName,
        refOrgNm: cRef.candidateReferenceOrganizationName,
        refdesig: cRef.candidateReferenceDesignationId,
        refEmail: cRef.candidateReferenceEmail,
        refContact: cRef.candidateReferenceContactNumber,
      }));
      setCRefDtl(e);
    }
  }, [currentCandRefDtl]);

  useEffect(() => {
    toggleContent();
    getPrefLoc();
    getEduDtl();
    getSkl();
    getExpDtl();
    getCertDtl();
    getRefDtl();
  }, [
    currentCand,
    toggleContent,
    currentCandPrefLoc,
    getPrefLoc,
    currentCandEduDtl,
    getEduDtl,
    currentCandSkils,
    getSkl,
    currentCandExpDtl,
    getExpDtl,
    currentCandCertDtl,
    getCertDtl,
    currentCandRefDtl,
    getRefDtl,
  ]);

  const [srcOfHrArr, setSrcOfHrArr] = useState([]);
  const [genderArr, setGenderArr] = useState([]);
  const [desgntnArr, setDesgntnArr] = useState([]);
  const [countryArr, setCountryArr] = useState([]);
  const [cityArr, setCityArr] = useState([]);
  const [qualArr, setQualArr] = useState([]);
  const [nmTitleArr, setNmTitleArr] = useState([]);
  const [universityArr, setUniversityArr] = useState([]);
  const [qualTypeArr, setQualTypeArr] = useState([]);
  const [certNmArr, setCertNmArr] = useState([]);
  const [certInstNmArr, setCertInstNmArr] = useState([]);
  const [streamArr, setStreamArr] = useState([]);
  const [skillArr, setSkillArr] = useState([]);
  const [secSkillArr, setSecSkillArr] = useState([]);
  const [priSkillArr, setPriSkillArr] = useState([]);
  const [uploadClick, setUploadClick] = useState(true);
  const [uniqueNumber, setUniqueNumber] = useState('');
  const uniqueNum = ['AADHAR', 'PAN'];

  const yesNoArr = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  const truncateFileName = (fileName, maxLength) => {
    if (fileName.length <= maxLength) return fileName;
    const start = fileName.substring(0, maxLength / 2);
    const end = fileName.substring(fileName.length - maxLength / 2);
    return `${start}...${end}`;
  };

  const handleFetchedData = () => {
    const fetchMasterData = axiosInstance.get(
      `/rest/candidate/fetchMastersData`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const fetchValidationData = axiosInstance.get(
      `/rest/candidate/fetchCandValidtionData`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Call both APIs in parallel
    Promise.all([fetchMasterData, fetchValidationData])
      .then(([masterDataRes, validationDataRes]) => {
        // Extracting data from responses
        const masterData = masterDataRes.data;
        const validationData = validationDataRes.data;

        // Set data for master data
        setSrcOfHrArr(masterData.scrOfhireData);
        setGenderArr(masterData.genderData);
        setSkillArr(masterData.skillData);
        setDesgntnArr(masterData.desginationData);
        setQualArr(masterData.qualData);
        setNmTitleArr(masterData.nmTitleData);
        setCountryArr(masterData.countriesData);
        setCityArr(masterData.citiesData);
        setUniversityArr(masterData.universityData);
        setQualTypeArr(masterData.qualTypData);
        setStreamArr(masterData.streamData);

        // Clone and sort the certificate arrays
        const sortedCertNmArr = [...masterData.certNm].sort((a, b) => a.localeCompare(b));
        const sortedCertInstNmArr = [...masterData.certInst].sort((a, b) => a.localeCompare(b));

        // Set sorted arrays
        setCertNmArr(sortedCertNmArr);
        setCertInstNmArr(sortedCertInstNmArr);

        // Set data for validation data
        setCandEmailArr(validationData.candEmails);
        setCandUniqNoArr(validationData.candUnqNo);

        // Get the country code (e.g., 'IN' or 'US') from the subscription site
        const cur = subscr.subscrSite.subscrCountry;

        // Check if the country exists and if currentsalcurncy is not already set
        if (cur) {
          // If the country currency is already set, don't reset
          if (!getValues('currentsalcurncy')) {
            const countryCurrency = masterData.countriesData.find(
              (item) => item.currencySymbol === cur.currencySymbol
            );

            // If the country's currency is found, set it
            if (countryCurrency) {
              setValue('currentsalcurncy', countryCurrency, { shouldValidate: true });
            } else {
              // If the country currency symbol doesn't exist, default to Indian currency (₹)
              // const indianCurrency = masterData.countriesData.find(
              //   (item) => item.currencySymbol === '₹' // Default to Indian currency symbol
              // );
              const currCur = cur;
              setValue('currentsalcurncy', currCur, { shouldValidate: true });
            }
          }

          if (!getValues('exptsalcurncy')) {
            const countryCurrency = masterData.countriesData.find(
              (item) => item.currencySymbol === cur.currencySymbol
            );

            // If the country's currency is found, set it
            if (countryCurrency) {
              setValue('exptsalcurncy', countryCurrency, { shouldValidate: true });
            } else {
              // If the country currency symbol doesn't exist, default to Indian currency (₹)
              // const indianCurrency = masterData.countriesData.find(
              //   (item) => item.currencySymbol === '₹' // Default to Indian currency symbol
              // );
              const indCurr = cur;
              setValue('exptsalcurncy', indCurr, { shouldValidate: true });
            }
          }

          // Check if 'phncode' already has a value; if not, set the phone code from sessionStorage
          if (!getValues('phncode')) {
            // If phone code exists in sessionStorage, use it, otherwise use a default value
            const phoneCode = cur;

            // Set 'phncode' to the value from sessionStorage or the default
            setValue('phncode', phoneCode, { shouldValidate: true });
          }

          setValue('curr', cur, { shouldValidate: true });
          // setValue('phncode', cur, { shouldValidate: true });
        }
      })
      .catch((err) => {
        console.error(err);
        handleApiError(err);
      });
  };

  // use effrct for fetching data
  useEffect(() => {
    handleFetchedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultValues = useMemo(
    () => ({
      avatarUrl: isProfilePhotoRemoved ? null : (currentCand?.profilePhoto || null),
      resumeUpload: null,
      srcofhire: currentCand?.sourceOfHire || null,
      srcofhireNm: currentCand?.sourceOfHireName || '',
      fname: currentCand?.firstName || '',
      salutationName: currentCand?.salutationName || {
        nameTitleId: 1,
        salutationName: 'Mr',
        subscrSiteId: {
          subscrSiteId: 1,
          subscr: {},
          subscrSiteName: 'pune',
          startDate: '2023-07-17T12:34:56.789Z',
          endDate: '2023-07-17T12:34:56.789Z',
          createdBy: {
            userId: 1,
            userName: 'vw',
            userPassword: 'vw@123',
            startDate: '2023-07-17T12:34:56.789Z',
            endDate: '2023-07-17T12:34:56.789Z',
            createdDate: '2023-07-17T12:34:56.789Z',
            createdBy: 1,
            lastUpdatedDate: '2023-07-17T12:34:56.789Z',
            lastUpdatedBy: 1,
          },
          createdDate: '2023-07-17T12:34:56.789Z',
          lastUpdatedBy: {
            userId: 1,
            userName: 'vw',
            userPassword: 'vw@123',
            startDate: '2023-07-17T12:34:56.789Z',
            endDate: '2023-07-17T12:34:56.789Z',
            createdDate: '2023-07-17T12:34:56.789Z',
            createdBy: 1,
            lastUpdatedDate: '2023-07-17T12:34:56.789Z',
            lastUpdatedBy: 1,
          },
          lastUpdatedDate: '2023-07-17T12:34:56.789Z',
        },
        startDate: '2023-07-28T05:45:53.888Z',
        endDate: null,
        createdBy: {
          userId: 1,
          userName: 'vw',
          userPassword: 'vw@123',
          startDate: '2023-07-17T12:34:56.789Z',
          endDate: '2023-07-17T12:34:56.789Z',
          createdDate: '2023-07-17T12:34:56.789Z',
          createdBy: 1,
          lastUpdatedDate: '2023-07-17T12:34:56.789Z',
          lastUpdatedBy: 1,
        },
        createdDate: '2023-07-28T05:45:53.888Z',
        lastUpdatedBy: {
          userId: 1,
          userName: 'vw',
          userPassword: 'vw@123',
          startDate: '2023-07-17T12:34:56.789Z',
          endDate: '2023-07-17T12:34:56.789Z',
          createdDate: '2023-07-17T12:34:56.789Z',
          createdBy: 1,
          lastUpdatedDate: '2023-07-17T12:34:56.789Z',
          lastUpdatedBy: 1,
        },
        lastUpdatedDate: '2023-07-28T05:45:53.888Z',
      },
      mname: currentCand?.middleName || '',
      lname: currentCand?.lastName || '',
      email: currentCand?.email || '',
      mobno: currentCand?.mobileNumber || '',
      phncode: currentCand?.mobPhoneCode || null,
      gender: currentCand?.gender || null,
      cuurntloc: currentCand?.currentLocationCity || null,
      prefloc: currentCandPrefLoc ? cPrefLoc : [],
      readytolocate: currentCand?.readyToRelocate || 'false',
      panno: currentCand?.panNo || '',
      linkedinUrl: currentCand?.linkedinUrl || '',
      expinyrs: currentCand?.experienceInYears || 0.0,
      relexp: currentCand?.relevantExperience || 0.0,
      candQal: currentCand?.qualification || null,
      currentjob: currentCand?.currentJobTitle || null,
      currentcomp: currentCand?.currentEmployer || '',
      noticeprd: currentCand?.noticePeriod || 0,
      currentsal: currentCand?.currentSalary || 0,
      currentsalcurncy: currentCand?.currentSalaryCurrency || null,
      exptsal: currentCand?.expectedSalary || 0,
      exptsalcurncy: currentCand?.expectedSalaryCurrency || null,
      primaryskl: currentCandSkils ? cPskl : [],
      secondskl: currentCandSkils ? cSskl : [],
      hasresigned: currentCand?.hasResigned || 'false',
      iscurrentemp: currentCand?.currentEmployee || 'false',
      uniqueNum: currentCand?.uniqueNoType || '',
      uniqueno: currentCand?.panNo || '',
      resigneddate: currentCand?.resignDate ? new Date(currentCand.resignDate) : null,
      canjoinby: currentCand?.canJoinBy ? new Date(currentCand.canJoinBy) : null,
      candExp: currentCandExpDtl ? cExpDtl : [],

      candCert: currentCandCertDtl ? cCertDtl : [],

      candEdu: currentCandEduDtl ? cEduDtl : [],

      candRef: currentCandRefDtl ? cRefDtl : [],

      subscrSiteId: 1,
      createdBy: 1,
      createdDate: new Date(),
      lastUpdatedBy: 1,
      lastUpdatedDate: new Date(),
    }),
    [
      currentCand,
      currentCandPrefLoc,
      cPrefLoc,
      currentCandEduDtl,
      cEduDtl,
      currentCandSkils,
      cPskl,
      cSskl,
      currentCandExpDtl,
      cExpDtl,
      currentCandCertDtl,
      cCertDtl,
      currentCandRefDtl,
      cRefDtl,
      isProfilePhotoRemoved,
    ]
  );
  // onchange Method
  const methods = useForm({
    resolver: yupResolver(NewCandidateSchema),
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues,
  });
  const {
    control,
    handleSubmit,
    watch,
    resetField,
    getValues,
    setValue,
    reset,
    formState: { errors },
    trigger,
  } = methods;

  useEffect(() => {
    if (currentCand?.profilePhoto) {
      setValue('avatarUrl', currentCand.profilePhoto);
    }
  }, [currentCand, setValue]);

  const filterPhoneCode = createFilterOptions({
    // matchFrom: 'start',
    stringify: (option) => `${option.countryName} (${option.phoneCode})`,
    // stringify: (option) => option.phoneCode,
  });
  const filterCurrency = createFilterOptions({
    // matchFrom: 'start',
    stringify: (option) => `${option.countryName} (${option.currencySymbol})`,
    // stringify: (option) => option.phoneCode,
  });

  useEffect(
    () => {
      if (currentCand) {
        reset(defaultValues);
        setUniqueNumber(currentCand.uniqueNoType);

        if (currentCand.profilePhoto) {
          setCurCandPic(true);
        }

        if (!disabled) {
          const savedStep = localStorage.getItem(`activeStep_${candidateId}`);
          if (savedStep) {
            setActiveStep(parseInt(savedStep, 10)); // Load from localStorage if available
          }
        }
      } else {
        const savedStep = localStorage.getItem('activeStepKey');
        setActiveStep(savedStep ? parseInt(savedStep, 10) : 0);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCand, reset, defaultValues]
  );

  // ------------------------------------------------------------------------------------------------------------------------------------
  // Resume Upload
  const handleRemoveResume = useCallback(() => {
    setValue('resumeUpload', null);
    setRFileNm('');
    setResumeTyp('');
    if (currentCand) {
      setEditResume(true);
      setUploadClick(true);
      setResumeTyp(currentCand.resume.split('.').pop().toLowerCase());
    }
  }, [setValue, setRFileNm, currentCand, setEditResume]);

  const handleDropResume = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      setEditResume(false);

      setRFileNm(truncateFileName(file.name, 40));
      setResumeTyp(file.type);

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (!currentCand) {
        setFileToAutoFill(newFile);
        quickAutoFill.onTrue();
      }
      if (newFile) {
        setValue('resumeUpload', newFile, { shouldValidate: true });
      }
    },
    [setValue, setRFileNm, setEditResume, currentCand, quickAutoFill, setFileToAutoFill]
  );

  const handleAutoFillResume = useCallback(async () => {
    const cur = subscr.subscrSite.country;

    setIsResumeProcessing(true);
    const formData = new FormData();
    formData.append('file', fileToAutoFill);
    const parsRsmUrl = `/rest/candidate/getResumeTxt`;
    await axiosInstance
      .post(parsRsmUrl, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        setValue('fname', res.data.fetchedCandData.fname);
        setValue('lname', res.data.fetchedCandData.lname);
        setValue('email', res.data.fetchedCandData.email);
        setValue('mobno', res.data.fetchedCandData.phone);
        setValue('phncode', res.data.fetchedCandData.phoneCode);

        // Then, check if 'phncode' has a value
        if (!getValues('phncode')) {
          // If not, use phone code from sessionStorage or default
          const phoneCode = cur; // 'cur' should be defined somewhere, likely sessionStorage.getItem or default

          // Set the value with validation trigger
          setValue('phncode', phoneCode, { shouldValidate: true });
        }

        setValue('cuurntloc', res.data.fetchedCandData.currentLocation);
        setValue('linkedinUrl', res.data.fetchedCandData.linkedinUrl);
        setValue('candQal', res.data.fetchedCandData.highestQualification);
        setValue('currentjob', res.data.fetchedCandData.currentDesignation);
        setValue('currentcomp', res.data.fetchedCandData.currentEmployer);
        setValue('primaryskl', res.data.fetchedCandSklData);
        if (res.data.fetchedCandEduData) {
          const e = res.data.fetchedCandEduData.map((cEdu) => ({
            eduSrno: 0,
            university: cEdu.university,
            othrUni: cEdu.otherUni,
            qualtype: cEdu.degreeType,
            qualification: cEdu.degree,
            stream: null,
            candpercnt: cEdu.percentage,
            candcgpa: cEdu.cgpa,
            currentypurs: '',
            edufromDate: cEdu.fromDate ? new Date(cEdu.fromDate) : null,
            edutoDate: cEdu.toDate ? new Date(cEdu.toDate) : null,
          }));
          setValue('candEdu', e);
        }
        if (res.data.fetchedCandExpData) {
          const e = res.data.fetchedCandExpData.map((cExp) => ({
            expSrno: 0,
            compNm: cExp.companyName,
            designation: cExp.designation,
            ctc: 0,
            ctccurncy: null,
            country: null,
            currentywrkng: false,
            expfromDate: cExp.fromDate ? new Date(cExp.fromDate) : null,
            exptoDate: cExp.toDate ? new Date(cExp.toDate) : null,
          }));
          setValue('candExp', e);
          setSwitchState((prevState) => ({
            ...prevState,
            experience: true, // Set experience to true if length > 0
          }));
        }
        trigger();
      })
      .catch((error) => {
        console.error('Error processing file:', error);
        handleApiError(error);
      })
      .finally(() => {
        setIsResumeProcessing(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileToAutoFill, setValue, token]);


  const downloadResume = async (resumePath) => {
    try {
      if (!resumePath) {
        enqueueSnackbar(
          'No resume found. Please upload a resume.',
          { variant: 'warning' }
        );
        return;
      }

      const { data } = await axiosInstance.get(
        '/rest/candidate/download-resume',
        {
          params: { path: resumePath },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (data.error) {
        enqueueSnackbar(data.error, { variant: 'warning' });
        return;
      }

      window.open(data.downloadUrl, '_blank', 'noopener');

    } catch (error) {
      const msg = error.response?.data?.error
        || error.response?.data?.message
        || 'Resume download failed. Please try again.';
      enqueueSnackbar(msg, { variant: 'error' });
      console.error('Resume download failed:', error);
    }
  };

  // --------------------------------------------------------------------------------------------------------------------------------------------
  function getResumeIcon() {
    switch (resumeTyp) {
      case 'pdf':
      case 'application/pdf':
        return <Iconify icon="vscode-icons:file-type-pdf2" color="#00a76f" width={35} />;
      case 'docx':
      case 'doc':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'application/msword':
        return <Iconify icon="vscode-icons:file-type-word" color="#00a76f" width={35} />;
      default:
        return <Iconify icon="flat-color-icons:document" width={35} />;
    }
  }

  const handleUploadClick = useCallback(() => {
    setUploadClick(false);

    fileInputRef.current.click();
  }, []);

  // Step 1: Initialize the flags at the parent component level
  const [isEduProcessed, setIsEduProcessed] = useState(false);
  const [isCertProcessed, setIsCertProcessed] = useState(false);
  const [isRefProcessed, setIsRefProcessed] = useState(false);
  const [isExpProcessed, setIsExpProcessed] = useState(false);
  const [isSkillsProcessed, setIsSkillsProcessed] = useState(false);

  // Example of resetting the flag when user navigates away from the section
  const handleStepChange = (newStep) => {
    if (newStep !== activeStep) {
      // Reset the flag if the user is moving to a different section
      setIsEduProcessed(false);
      setIsCertProcessed(false);
      setIsRefProcessed(false);
      setIsExpProcessed(false);
      setIsSkillsProcessed(false);
    }
    setActiveStep(newStep);
  };

  // ----------------------------------------------------------------------------------------------------------------------------------------

  const onSubmit = handleSubmit(async (data) => {
    loadingSend.onTrue();

    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      loadingSend.onFalse();
      const enctedId = await fetchEncryptedToken(data.createdBy, token);
      const encrypd = await fetchEncryptedToken(data.subscrSiteId, token);
      const encredId = await fetchEncryptedToken(data.lastUpdatedBy, token);

      const [createdByUser, subscrSite, lastUpdatedByD] = await Promise.all([
        axiosInstance.get(
          `/rest/users/singleuser/${enctedId
          }`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        axiosInstance.get(
          `/rest/subscrsite/singlesbscrst/${encrypd
          }`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        axiosInstance.get(
          `/rest/users/singleuser/${encredId
          }`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);
      console.info('DATA', data);

      const candData = {
        subscrSiteId: subscrSite.data,
        currentLocationCity: data.cuurntloc,
        gender: data.gender,
        salutationName: data.salutationName,
        qualification: data.candQal,
        sourceOfHire: data.srcofhire,
        firstName: data.fname,
        middleName: data.mname,
        lastName: data.lname,
        mobileNumber: data.mobno,
        mobPhoneCode: data.phncode,
        currentEmployer: data.currentcomp,
        currentJobTitle: data.currentjob,
        currentSalaryCurrency: data.currentsalcurncy,
        currentSalary: Math.round(data.currentsal),
        expectedSalaryCurrency: data.exptsalcurncy,
        expectedSalary: Math.round(data.exptsal),
        readyToRelocate: data.readytolocate,
        panNo: data.uniqueno,
        linkedinUrl: data.linkedinUrl,
        relevantExperience: data.relexp,
        noticePeriod: data.noticeprd,
        canJoinBy: data.canjoinby,
        hasResigned: data.hasresigned,
        resignDate: data.resigneddate,
        sourceOfHireName: data.srcofhireNm,
        experienceInYears: data.expinyrs,
        email: data.email,
        isFinalCand: data.isFinalStep || isFinalStep,
        currentEmployee: data.iscurrentemp,
        uniqueNoType: data.uniqueNum,


        createdDate: data.createdDate,
        lastUpdatedDate: data.lastUpdatedDate,
        profilePhoto: isProfilePhotoRemoved ? null : (currentCand?.profilePhoto || null),
      };
      // resume-upload
      if (currentCand) {
        if (data.resumeUpload) {
          // Resume update (with or without profile picture)
          const formData = new FormData();
          formData.append('candD', JSON.stringify(candData));
          formData.append('resumeF', data.resumeUpload);
          formData.append('candId', currentCand.candidateId);
          if (data.avatarUrl instanceof File) {
            formData.append('profileP', data.avatarUrl);
          }

          const updtUrl = `/rest/candidate/updtCandRsm`;
          await axiosInstance
            .put(updtUrl, formData, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
              // handle success
            });
        } else {
          // Only profile picture or candidate data updated, no resume
          const formData = new FormData();
          if (data.avatarUrl instanceof File) {
            formData.append('profileP', data.avatarUrl);
          }
          formData.append('candD', JSON.stringify(candData));

          const updtUrl = `/rest/candidate/updtCand/${currentCand.candidateId
            }`;
          await axiosInstance
            .put(updtUrl, formData, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
              // handle success
            });
        }
        if (activeStep === 3 || activeStep <= 6) {
          // Education Details
          if (!isEduProcessed) {
            // Check if data has already been processed

            if (currentCandEduDtl || data.candEdu) {
              const addedEdus = data.candEdu.filter((edu) => edu.eduSrno === 0);
              const updtEdus = data.candEdu.filter((edu) => edu.eduSrno !== 0);
              const removeEdus = cEduDtl.filter(
                (currEdu) => !updtEdus.some((edu) => edu.eduSrno === currEdu.eduSrno)
              );

              if (addedEdus.length > 0) {
                addedEdus.forEach(async (cEdu) => {
                  const cEduData = {
                    candidateId: currentCand,
                    university: cEdu.university,
                    universityName: cEdu.othrUni,
                    qualification: cEdu.qualification,
                    qualificationTypeId: cEdu.qualtype,
                    stream: cEdu.stream,
                    candidateQualPercentage: cEdu.candpercnt,
                    candidateQualCgpa: cEdu.candcgpa,
                    currentlyPursuing: cEdu.currentypurs,
                    candidateQualFromDate: cEdu.edufromDate,
                    candidateQualToDate: cEdu.edutoDate,
                    subscrSiteId: subscrSite.data,


                    createdDate: new Date(),
                    lastUpdatedDate: new Date(),
                  };
                  const addCandEduUrl = `/rest/candedu/addCandEdu`;
                  await axiosInstance
                    .post(addCandEduUrl, cEduData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res4) => { });
                });
              }
              if (removeEdus.length > 0) {
                removeEdus.forEach(async (remove) => {
                  if (remove?.eduSrno) {
                    await axiosInstance.delete(
                      `/rest/candedu/deleteEduData/${remove.eduSrno
                      }`,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );
                  } // Skip if eduSrno is 0
                });
              }
              if (updtEdus.length > 0) {
                updtEdus.forEach(async (cEdu) => {
                  const cEduData = {
                    university: cEdu.university,
                    universityName: cEdu.othrUni,
                    qualification: cEdu.qualification,
                    qualificationTypeId: cEdu.qualtype,
                    stream: cEdu.stream,
                    candidateQualPercentage: cEdu.candpercnt,
                    candidateQualCgpa: cEdu.candcgpa,
                    currentlyPursuing: cEdu.currentypurs,
                    candidateQualFromDate: cEdu.edufromDate,
                    candidateQualToDate: cEdu.edutoDate,

                    lastUpdatedDate: new Date(),
                  };
                  const updtCandEdUrl = `/rest/candedu/updateEduData/${cEdu.eduSrno
                    }`;
                  await axiosInstance
                    .put(updtCandEdUrl, cEduData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res5) => { });
                });
              }
            }
            setIsEduProcessed(true);
          }
        }
        // certification Details
        if (activeStep === 4 || activeStep <= 6) {
          if (!isCertProcessed) {
            if (currentCandCertDtl || data.candCert) {
              const addedCerts = data.candCert.filter((cert) => cert.cerSrno === 0);
              const updtCerts = data.candCert.filter((cert) => cert.cerSrno !== 0);
              const removeCerts = cCertDtl.filter(
                (currCrt) => !updtCerts.some((crt) => crt.cerSrno === currCrt.cerSrno)
              );

              if (addedCerts.length > 0) {
                addedCerts.forEach(async (cCert) => {
                  const cCertData = {
                    candidateId: currentCand,
                    certificationName: cCert.certiNm,
                    instituteName: cCert.certiInstiNm,
                    durationFrom: cCert.certfromDate,
                    durationTo: cCert.certtoDate,
                    subscrSiteId: subscrSite.data,


                    createdAt: new Date(),
                    lastUpdatedAt: new Date(),
                  };
                  const candCertUrl = `/rest/candcert/AddCandCert`;
                  await axiosInstance
                    .post(candCertUrl, cCertData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res6) => { });
                });
              }
              if (removeCerts.length > 0) {
                removeCerts.forEach(async (remove) => {
                  if (remove?.cerSrno) {
                    await axiosInstance.delete(
                      `/rest/candcert/deleteCertDtl/${remove.cerSrno
                      }`,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );
                  }
                });
              }
              if (updtCerts.length > 0) {
                updtCerts.forEach(async (cCert) => {
                  const cCertData = {
                    certificationName: cCert.certiNm,
                    instituteName: cCert.certiInstiNm,
                    durationFrom: cCert.certfromDate,
                    durationTo: cCert.certtoDate,
                    subscrSiteId: subscrSite.data,

                    lastUpdatedAt: new Date(),
                  };

                  const updtCandCrtUrl = `/rest/candcert/updateCertDtl/${cCert.cerSrno
                    }`;
                  await axiosInstance
                    .put(updtCandCrtUrl, cCertData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res5) => { });
                });
              }
            }
            setIsCertProcessed(true);
          }
        }
        // reference Details
        if (activeStep === 6) {
          if (!isRefProcessed) {
            if (currentCandRefDtl || data.candRef) {
              const addedRefDtl = data.candRef.filter((ref) => ref.refSrno === 0);
              const updtRefDtl = data.candRef.filter((ref) => ref.refSrno !== 0);
              const removeRefDtl = cRefDtl.filter(
                (currRef) => !updtRefDtl.some((ref) => ref.refSrno === currRef.refSrno)
              );
              if (addedRefDtl.length > 0) {
                addedRefDtl.forEach(async (cRef) => {
                  const cRefData = {
                    candidate: currentCand,
                    candidateReferenceName: cRef.refNm,
                    candidateReferenceOrganizationName: cRef.refOrgNm,
                    candidateReferenceDesignationId: cRef.refdesig,
                    candidateReferenceEmail: cRef.refEmail,
                    candidateReferenceContactNumber: cRef.refContact,
                    subscrSiteId: subscrSite.data,


                    createdDate: new Date(),
                    lastUpdatedDate: new Date(),
                  };

                  const candRefUrl = `/rest/candref/addCandRef`;
                  await axiosInstance
                    .post(candRefUrl, cRefData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res7) => { });
                });
              }
              if (removeRefDtl.length > 0) {
                removeRefDtl.forEach(async (remove) => {
                  if (remove?.refSrno) {
                    await axiosInstance.delete(
                      `/rest/candref/deleteRef/${remove.refSrno
                      }`,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );
                  }
                });
              }
              if (updtRefDtl.length > 0) {
                updtRefDtl.forEach(async (cRef) => {
                  const cRefData = {
                    candidateReferenceName: cRef.refNm,
                    candidateReferenceOrganizationName: cRef.refOrgNm,
                    candidateReferenceDesignationId: cRef.refdesig,
                    candidateReferenceEmail: cRef.refEmail,
                    candidateReferenceContactNumber: cRef.refContact,

                    lastUpdatedDate: new Date(),
                  };
                  const updtCandRefUrl = `/rest/candref/updtRef/${cRef.refSrno
                    }`;
                  await axiosInstance
                    .put(updtCandRefUrl, cRefData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res5) => { });
                });
              }
            }
            setIsRefProcessed(true);
          }
        }
        // Experience Details
        if (activeStep === 5 || activeStep <= 6) {
          if (!isExpProcessed) {
            if (currentCandExpDtl || data.candExp) {
              // To set 'experience' to true
              const addedExpDtl = data.candExp.filter((exp) => exp.expSrno === 0);
              const updtExpDtl = data.candExp.filter((exp) => exp.expSrno !== 0);
              const removeExpDtl = cExpDtl.filter(
                (currExp) => !updtExpDtl.some((exp) => exp.expSrno === currExp.expSrno)
              );

              if (addedExpDtl.length > 0) {
                addedExpDtl.forEach(async (cExp) => {
                  const cExpData = {
                    candidateId: currentCand,
                    candidateExperienceCompanyName: cExp.compNm,
                    candidateDesignation: cExp.designation,
                    candidateCtcCurrency: cExp.ctccurncy,
                    candidateCtc: cExp.ctc,
                    candidateCurrentlyWorking: cExp.currentywrkng,
                    candidateExperienceFromDate: cExp.expfromDate,
                    candidateExperienceToDate: cExp.exptoDate,
                    candidateExperienceCountryid: cExp.country,
                    subscrSiteId: subscrSite.data,


                    createdDate: new Date(),
                    lastUpdatedDate: new Date(),
                  };
                  const candExpUrl = `/rest/candexp/addCandExp`;
                  await axiosInstance
                    .post(candExpUrl, cExpData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res5) => { });
                });
              }
              if (removeExpDtl.length > 0) {
                removeExpDtl.forEach(async (remove) => {
                  if (remove?.expSrno) {
                    await axiosInstance.delete(
                      `/rest/candexp/deleteCandExp/${remove.expSrno
                      }`,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );
                  }
                });
              }
              if (updtExpDtl.length > 0) {
                updtExpDtl.forEach(async (cExp) => {
                  const cExpData = {
                    candidateExperienceCompanyName: cExp.compNm,
                    candidateDesignation: cExp.designation,
                    candidateCtcCurrency: cExp.ctccurncy,
                    candidateCtc: cExp.ctc,
                    candidateCurrentlyWorking: cExp.currentywrkng,
                    candidateExperienceFromDate: cExp.expfromDate,
                    candidateExperienceToDate: cExp.exptoDate,
                    candidateExperienceCountryid: cExp.country,
                    subscrSiteId: subscrSite.data,

                    lastUpdatedDate: new Date(),
                  };
                  const updtCandExpUrl = `/rest/candexp/updtCandExp/${cExp.expSrno
                    }`;
                  await axiosInstance
                    .put(updtCandExpUrl, cExpData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then((res5) => { });
                });
              }
            }
          }
          setIsExpProcessed(true);
        }
        // Skills Sections
        if (activeStep === 2 || activeStep <= 6) {
          if (!isSkillsProcessed) {
            if (currentCandSkils) {
              if (cPskl) {
                const addedSkls = data.primaryskl.filter(
                  (skill) => !cPskl.some((currentSkill) => currentSkill.skillId === skill.skillId)
                );

                const removedSkls = cPskl.filter(
                  (currentSkill) =>
                    !data.primaryskl.some((skill) => skill.skillId === currentSkill.skillId)
                );

                if (removedSkls.length > 0) {
                  removedSkls.forEach(async (removed) => {
                    if (removed?.skillId) {
                      await axiosInstance.delete(
                        `/rest/candskills/deleteCandSkl/${removed.skillId
                        }/${currentCand.candidateId}`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      );
                    }
                  });
                }
                if (addedSkls.length > 0) {
                  addedSkls.forEach(async (added) => {
                    const psklD = {
                      candidateId: currentCand,
                      skill: added,
                      skillType: 'Primary',
                      subscrSiteId: subscrSite.data,


                      createdDate: data.createdDate,
                      lastUpdatedDate: data.lastUpdatedDate,
                    };
                    const addPsklUrl = `/rest/candskills/addCandSkill`;
                    await axiosInstance
                      .post(addPsklUrl, psklD, {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      })
                      .then((res1) => { });
                  });
                }
              }
              if (cSskl) {
                const addedSkls = data.secondskl.filter(
                  (skill) => !cSskl.some((currentSkill) => currentSkill.skillId === skill.skillId)
                );

                const removedSkls = cSskl.filter(
                  (currentSkill) =>
                    !data.secondskl.some((skill) => skill.skillId === currentSkill.skillId)
                );

                if (removedSkls.length > 0) {
                  removedSkls.forEach(async (removed) => {
                    if (removed?.skillId) {
                      await axiosInstance.delete(
                        `/rest/candskills/deleteCandSkl/${removed.skillId
                        }/${currentCand.candidateId}`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      );
                    }
                  });
                }
                if (addedSkls.length > 0) {
                  addedSkls.forEach(async (added) => {
                    const psklD = {
                      candidateId: currentCand,
                      skill: added,
                      skillType: 'Secondary',
                      subscrSiteId: subscrSite.data,


                      createdDate: data.createdDate,
                      lastUpdatedDate: data.lastUpdatedDate,
                    };
                    const addPsklUrl = `/rest/candskills/addCandSkill`;
                    await axiosInstance
                      .post(addPsklUrl, psklD, {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      })
                      .then((res1) => { });
                  });
                }
              }
            }
          }
          setIsSkillsProcessed(true); // After processing, set to true
        }
        // Preferred Location Details
        if (currentCandPrefLoc) {
          const addedLoc = data.prefloc.filter(
            (loc) => !cPrefLoc.some((currentPLoc) => currentPLoc.cityId === loc.cityId)
          );
          const removedLoc = cPrefLoc.filter(
            (currentPLoc) => !data.prefloc.some((loc) => loc.cityId === currentPLoc.cityId)
          );
          if (removedLoc.length > 0) {
            removedLoc.forEach(async (removed) => {
              await axiosInstance.delete(
                `/rest/candprefloc/deleteCandPLoc/${removed.cityId
                }/${currentCand.candidateId}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
            });
          }
          if (addedLoc.length > 0) {
            addedLoc.forEach(async (added) => {
              const pLocD = {
                candidateId: currentCand,
                prefLocCity: added,
                subscrSiteId: subscrSite.data,


                createdDate: data.createdDate,
                lastUpdatedDate: data.lastUpdatedDate,
              };
              const addPLocUrl = `/rest/candprefloc/addCandPrefLoc`;
              await axiosInstance
                .post(addPLocUrl, pLocD, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res3) => { });
            });
          }
        }
        enqueueSnackbar('Candidate Updated Successfully!');
      } else {
        //  add candidate Apis

        const formData = new FormData();
        formData.append('candD', JSON.stringify(candData));
        formData.append('resumeF', data.resumeUpload);
        formData.append('profileP', data.avatarUrl);

        const addCandUrl = `/rest/candidate/addCand`;
        await axiosInstance
          .post(addCandUrl, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          })
          .then((res) => {
            setRFileNm('');
            data.primaryskl.forEach(async (pskl) => {
              const psklD = {
                candidateId: res.data,
                skill: pskl,
                skillType: 'Primary',
                subscrSiteId: subscrSite.data,


                createdDate: data.createdDate,
                lastUpdatedDate: data.lastUpdatedDate,
              };
              const addPsklUrl = `/rest/candskills/addCandSkill`;
              await axiosInstance
                .post(addPsklUrl, psklD, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res1) => { });
            });
            data.secondskl.forEach(async (sskl) => {
              const ssklD = {
                candidateId: res.data,
                skill: sskl,
                skillType: 'Secondary',
                subscrSiteId: subscrSite.data,


                createdDate: data.createdDate,
                lastUpdatedDate: data.lastUpdatedDate,
              };
              const addSsklUrl = `/rest/candskills/addCandSkill`;
              await axiosInstance
                .post(addSsklUrl, ssklD, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res2) => { });
            });

            data.prefloc.forEach(async (pLoc) => {
              const pLocD = {
                candidateId: res.data,
                prefLocCity: pLoc,
                subscrSiteId: subscrSite.data,


                createdDate: data.createdDate,
                lastUpdatedDate: data.lastUpdatedDate,
              };
              const addPLocUrl = `/rest/candprefloc/addCandPrefLoc`;
              await axiosInstance
                .post(addPLocUrl, pLocD, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res3) => { });
            });

            data.candEdu.forEach(async (cEdu) => {
              const cEduData = {
                candidateId: res.data,
                university: cEdu.university,
                qualification: cEdu.qualification,
                qualificationTypeId: cEdu.qualtype,
                stream: cEdu.stream,
                candidateQualPercentage: cEdu.candpercnt,
                candidateQualCgpa: cEdu.candcgpa,
                currentlyPursuing: cEdu.currentypurs,
                candidateQualFromDate: cEdu.edufromDate,
                candidateQualToDate: cEdu.edutoDate,
                subscrSiteId: subscrSite.data,


                createdDate: new Date(),
                lastUpdatedDate: new Date(),
              };
              const addCandEduUrl = `/rest/candedu/addCandEdu`;
              await axiosInstance
                .post(addCandEduUrl, cEduData, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res4) => { });
            });

            data.candExp.forEach(async (cExp) => {
              const cExpData = {
                candidateId: res.data,
                candidateExperienceCompanyName: cExp.compNm,
                candidateDesignation: cExp.designation,
                candidateCtcCurrency: cExp.ctccurncy,
                candidateCtc: cExp.ctc,
                candidateCurrentlyWorking: cExp.currentywrkng,
                candidateExperienceFromDate: cExp.expfromDate,
                candidateExperienceToDate: cExp.exptoDate,
                candidateExperienceCountryid: cExp.country,
                subscrSiteId: subscrSite.data,


                createdDate: new Date(),
                lastUpdatedDate: new Date(),
              };
              const candExpUrl = `/rest/candexp/addCandExp`;
              await axiosInstance
                .post(candExpUrl, cExpData, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res5) => { });
            });

            data.candCert.forEach(async (cCert) => {
              const cCertData = {
                candidateId: res.data,
                certificationName: cCert.certiNm,
                instituteName: cCert.certiInstiNm,
                durationFrom: cCert.certfromDate,
                durationTo: cCert.certtoDate,
                subscrSiteId: subscrSite.data,


                createdDate: new Date(),
                lastUpdatedDate: new Date(),
              };
              const candCertUrl = `/rest/candcert/AddCandCert`;
              await axiosInstance
                .post(candCertUrl, cCertData, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res6) => { });
            });

            data.candRef.forEach(async (cRef) => {
              const cRefData = {
                candidate: res.data,
                candidateReferenceName: cRef.refNm,
                candidateReferenceOrganizationName: cRef.refOrgNm,
                candidateReferenceDesignationId: cRef.refdesig,
                candidateReferenceEmail: cRef.refEmail,
                candidateReferenceContactNumber: cRef.refContact,
                subscrSiteId: subscrSite.data,


                createdDate: new Date(),
                lastUpdatedDate: new Date(),
              };
              const candRefUrl = `/rest/candref/addCandRef`;
              await axiosInstance
                .post(candRefUrl, cRefData, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((res7) => { });
            });
            if (activeStep === steps.length - 1 || steps === 'Reference Details') {
              enqueueSnackbar('Candidate Created Successfully!', { variant: 'success' });
              handleSave(); // Save activeStep before API call
            } else {
              handleSave(); // Save activeStep before API call
              enqueueSnackbar('Draft saved successfully!', { variant: 'success' });
            }
            // enqueueSnackbar('Candidate Created Successfully!');
            router.push(
              paths.dashboard.talentAcquisition.master.EditCandidate(res.data.candidateId)
            );
          });
      }

      // enqueueSnackbar(
      //   currentCand ? 'Candidate Updated Successfully!' : 'Candidate Created Successfully!'
      // );
      // router.push(paths.dashboard.recruitment.list);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error);
      loadingSend.onFalse();
      handleApiError(error);
    }
  });

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      setCurCandPic(true);
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('avatarUrl', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const handleRemovePhoto = useCallback(() => {
    setIsProfilePhotoRemoved(true); // marks for deletion
    setCurCandPic(null); // removes preview
    setValue('avatarUrl', null); // clears input value
    setIsFileTooLarge(false); // reset validation
  }, [setValue]);

  // ------------------------------------------------------------------------------------------------------------------------------------------------------------------

  const isStepOptional = (step) => step === 1;

  const isStepSkipped = (step) => skipped?.has(step);

  const DynamicData = methods.getValues();

  const candidateId = currentCand?.candidateId;

  const handleSave = useCallback(
    () => {
      if (currentCand) {
        localStorage.setItem(`activeStep_${candidateId}`, activeStep.toString());
      } else {
        localStorage.setItem('activeStepKey', activeStep.toString());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeStep, currentCand]
  );

  const handleNext = async () => {
    // 1. Get the fields for the current step using stepsvalidator
    const currentStepFields = Object.keys(stepsvalidator[activeStep].fields); // Get fields for the current step

    // // 2. Trigger validation for the current step
    const isValid = await trigger(currentStepFields); // Trigger validation for the current step's fields

    if (activeStep === steps.length - 1) {
      enqueueSnackbar('Don’t forget to save your changes before proceeding to the next step!', {
        variant: 'warning',
        autoHideDuration: 3000, // Snackbar will auto-hide after 3 seconds
      });
    }

    setActiveStep((prev) => {
      const newStep = prev + 1;
      localStorage.setItem(`activeStep_${candidateId || 'new'}`, newStep.toString());
      return newStep;
    });
    // Move to the next step by calling `handleStepChange`

    handleStepChange(activeStep + 1);

    setStepCompletionStatus((prev) => {
      const newStatus = [...prev];
      newStatus[activeStep] = isValid;
      return newStatus;
    });
  };

  const handleBack = () => {
    setActiveStep((prev) => {
      const newStep = Math.max(prev - 1, 0);
      localStorage.setItem(`activeStep_${candidateId || 'new'}`, newStep.toString());
      return newStep;
    });
    handleStepChange(Math.max(activeStep - 1, 0));
  };

  const handleSaveNext = async () => {
    const currentStepFields = Object.keys(stepsvalidator[activeStep].fields);
    const isValid = await trigger(currentStepFields);

    if (!isValid) {
      enqueueSnackbar('Please complete all required fields before proceeding.', {
        variant: 'warning',
      });
      return;
    }

    const formData = methods.getValues();

    // Submit the form data (as draft or step-wise)
    await handleFormSubmission(formData, true);

    // Save the active step
    if (candidateId) {
      localStorage.setItem(`activeStep_${candidateId}`, (activeStep + 1).toString());
    } else {
      localStorage.setItem('activeStepKey', (activeStep + 1).toString());
    }

    // Move to the next step
    setActiveStep((prev) => prev + 1);
    handleStepChange(activeStep + 1);

    // Update step completion status
    setStepCompletionStatus((prev) => {
      const newStatus = [...prev];
      newStatus[activeStep] = true;
      return newStatus;
    });
  };

  const [changedSections, setChangedSections] = useState({
    basicDetails: false,
    personalDetails: false,
    professionalDetails: false,
    educationDetails: false,
    certificationDetails: false,
    experienceDetails: false,
    referenceDetails: false,
  });

  // Check if currentCand is provided and has a candidateId

  // If candidateId is not available, we are creating a new candidate
  if (!candidateId) {
    // Handle the new candidate case here if necessary
  }

  // Keys for saving and retrieving form data
  const activeStepKey = `activeStep_${candidateId || 'new'}`; // Key for activeStep, 'new' for new candidates

  const validateAllSteps = async () => {
    const newStepStatus = [...stepCompletionStatus]; // Copy previous status

    const validationResults = await Promise.all(
      stepsvalidator.map(async (step, index) => {
        const fieldKeys = Object.keys(step.fields || {});
        if (fieldKeys.length > 0) {
          const isValid = await trigger(fieldKeys);
          newStepStatus[index] = isValid;
          return isValid;
        }
        newStepStatus[index] = true; // If no fields, consider it valid
        return true;
      })
    );

    // Update the step status based on validation results
    setStepCompletionStatus(newStepStatus);

    // Return final status (true if all steps are valid)
    const allValid = validationResults.every((result) => result === true);
    return allValid;
  };

  const handleFormSubmission = async (data) => {
    loadingSend.onTrue();
    try {
      const isValid = await validateAllSteps();
      if (isValid) {
        await onSubmit(data);
        localStorage.removeItem(activeStepKey);
      } else {
        enqueueSnackbar('Please complete all required steps before submitting.', {
          variant: 'warning',
        });
      }
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      loadingSend.onFalse();
    }
  };

  const memoizedMethods = useCallback(methods, [methods]);

  useEffect(
    () => {
      if (!candidateId) {
        // New candidate flow
        const savedData = localStorage.getItem('stepperFormData_new');
        const savedStep = localStorage.getItem('activeStep_new');

        if (savedData) {
          const parsedData = JSON.parse(savedData);
          Object.keys(parsedData).forEach((key) => {
            methods.setValue(key, parsedData[key]);
          });
        }

        setActiveStep(savedStep ? parseInt(savedStep, 10) : 0);
        return;
      }

      // Existing candidate flow
      if (disabled) {
        setActiveStep(0);
        return;
      }

      const savedStep = localStorage.getItem(`activeStep_${candidateId}`);
      if (savedStep) {
        setActiveStep(parseInt(savedStep, 10));
      }

      const savedFormData = JSON.parse(localStorage.getItem(`stepperFormData_${candidateId}`));
      if (savedFormData) {
        Object.keys(savedFormData).forEach((key) => {
          methods.setValue(key, savedFormData[key]);
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods, disabled]
  );

  // Common handler for both switches
  const handleSwitchChange = (event, section) => {
    setSwitchState((prevState) => ({
      ...prevState,
      [section]: event.target.checked,
    }));
  };

  const maxSizeOfDocs = useSelector((state) => state.tenantPreference?.maxSizeOfDocuments || 3);

  return (
    <>
      {/* <LinearAlternativeLabel /> */}

      {isResumeProcessing && (
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isResumeProcessing}
        >
          <Stack alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" mt={2}>
              Processing resume, It will take some time please wait...
            </Typography>
          </Stack>
        </Backdrop>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          color="secondary"
          variant="soft"
          startIcon={<Iconify icon="carbon:view" />}
          onClick={() => router.push(paths.dashboard.talentAcquisition.master.ListCandidate)}
        >
          {/* <img src="/assets/icons/menu-items/mdi--eye.png" alt="view eye" className="menu-item-img" /> */}
          Candidate List
        </Button>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 1, padding: '0px !important', marginTop: '8px' }}>
        {steps.map((label, index) => (
          <Step
            key={index}
            sx={{
              '&:hover': { cursor: 'pointer', backgroundColor: '#f2f3f4', borderRadius: '8px' },
            }}
          >
            <StepLabel
              StepIconProps={{
                completed: stepCompletionStatus[index] === true && index < activeStep,
                icon: (() => {
                  if (index === activeStep) {
                    return activeStep + 1;
                  }
                  if (stepCompletionStatus[index] === true) {
                    return index + 1;
                  }
                  if (stepCompletionStatus[index] === false && index <= activeStep) {
                    return (
                      <span
                        style={{
                          backgroundColor: '#f2f3f4', // 🟡 Yellow background
                          borderRadius: '50%', // ⭕ Circle shape
                          width: '24px', // Adjust size
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        <img
                          src="/assets/icons/menu-items/material-symbols--exclamation.png"
                          alt="❗"
                          style={{ height: '25px', width: '25px' }}
                        />
                      </span>
                    );
                  }
                  return index + 1; // Default: Show step number
                })(),
              }}
              sx={{
                fontWeight: 200,
                cursor: 'pointer',
                borderRadius: '8px',
                position: 'sticky',
                padding: '0px !important',
                transition: 'background-color 0.3s ease',
                '&:hover': { color: 'primary.dark', cursor: 'pointer', backgroundColor: '#f2f3f4' },
              }}
              onClick={async () => {
                if (index === activeStep) return; // ✅ Prevent revalidating the same step

                const newStatus = [...stepCompletionStatus];

                const validationNeeded =
                  index > activeStep ? [activeStep, index] : [index, activeStep];

                await Promise.all(
                  stepsvalidator
                    .slice(validationNeeded[0], validationNeeded[1] + 1)
                    .map(async (step, i) => {
                      const fieldKeys = Object.keys(step.fields || {});
                      if (fieldKeys.length > 0) {
                        const isValid = await trigger(fieldKeys);
                        newStatus[validationNeeded[0] + i] = isValid;
                      }
                    })
                );

                setStepCompletionStatus(newStatus);
                setActiveStep(index);

                if (!stepCompletionStatus[index]) {
                  console.log(
                    `Step ${index + 1} is not complete. Please fill all required fields.`
                  );
                }

                if (candidateId) {
                  localStorage.setItem(`activeStep_${candidateId}`, index.toString());
                }
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card
        sx={{
          p: 3,
          width: '100%', // Scale the form to 75% of the container width
          margin: 'auto', // Center the form horizontally
          // transform: 'scale(0.98)', // Scale down the entire form
          transformOrigin: 'top center', // Maintain scale position
        }}
      >
        <FormProvider methods={methods} disabled={disabled}>
          <Grid container spacing={3}>
            <Grid xs={12} md={12}>
              {activeStep === 0 && (
                <>
                  <Box sx={{ mb: 1 }}>
                    <RHFUploadAvatar
                      name="avatarUrl"
                      maxSize={maxSizeOfDocs}
                      onDrop={handleDrop}
                      helperText={
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 3,
                            mx: 'auto',
                            display: 'block',
                            textAlign: 'center',
                            color: 'text.disabled',
                          }}
                        >
                          Supported file formats: *.jpeg, *.jpg, *.png, *.gif
                          <br /> Max upload file size: {maxSizeOfDocs} MB
                        </Typography>
                      }
                    />

                    {!disabled &&
                      !isFileTooLarge &&
                      (curCandPic || (currentCand?.profilePhoto && !isProfilePhotoRemoved)) && (
                        <Typography
                          variant="caption"
                          onClick={() => {
                            handleRemovePhoto();
                          }}
                          sx={{
                            cursor: 'pointer',
                            color: 'blue',
                            textDecoration: 'underline',
                            mx: 'auto',
                            textAlign: 'center',
                            display: 'block',
                          }}
                        >
                          Remove Photo
                        </Typography>
                      )}
                  </Box>

                  <br />
                  <Box
                    rowGap={3}
                    columnGap={2}
                    display="grid"
                    gridTemplateColumns={{
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    {currentCand ? (
                      <>
                        <Box
                          sx={{
                            display: uploadClick ? 'flex' : 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              opacity: 0.72,
                            },
                            m: 0.5,
                            py: 1,
                            flexShrink: 0,
                            color: 'text.disabled',
                          }}
                        >
                          <Button
                            onClick={() => {
                              confirm.onTrue();
                            }}
                            sx={{
                              width: '100%',
                              height: '56px',
                              // bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                              border: 'solid 1px rgba(145, 158, 171, 0.2)',
                              borderRadius: 1.5,
                              '&:hover': {
                                border: 'solid 1px rgb(0, 0, 0)',
                                bgcolor: 'white',
                              },
                            }}
                          >
                            <Box
                              rowGap={3}
                              columnGap={2}
                              display="flex"
                              alignItems="center"
                              sx={{ color: 'text.disabled' }}
                              gridTemplateColumns={{
                                xs: 'repeat(1, 1fr)',
                                sm: 'repeat(2, 1fr)',
                              }}
                            >
                              <Iconify
                                icon="eva:cloud-upload-fill"
                                style={{ width: '40px', height: '24px !important' }}
                              />
                              <Typography variant="body2">
                                Upload Resume
                                <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                              </Typography>
                            </Box>
                          </Button>
                        </Box>
                        <Box sx={{ display: uploadClick ? 'none' : 'block !important' }}>
                          <RHFUploadBox
                            name="resumeUpload"
                            onDrop={handleDropResume}
                            acceptFile={{
                              'application/pdf': [],
                              'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                [],
                              'application/msword': [],
                              'text/plain': [],
                              'application/vnd.ms-powerpoint': [],
                              'application/vnd.openxmlformats-officedocument.presentationml.slideshow':
                                [],
                              'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                                [],
                            }}
                            onDelete={() =>
                              setValue('resumeUpload', null, { shouldValidate: true })
                            }
                            minSize={1000} // give value in byte
                            maxSize={3000000}
                            placeholder={
                              <Box
                                rowGap={3}
                                columnGap={2}
                                display="flex"
                                alignItems="center"
                                sx={{ color: 'text.disabled' }}
                                gridTemplateColumns={{
                                  xs: 'repeat(1, 1fr)',
                                  sm: 'repeat(2, 1fr)',
                                }}
                              >
                                <Iconify
                                  icon="eva:cloud-upload-fill"
                                  style={{ width: '40px', height: '24px !important' }}
                                />
                                <Typography variant="body2" ref={fileInputRef}>
                                  Upload Resume
                                  <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                                </Typography>
                              </Box>
                            }
                            sx={{
                              py: 1,
                              width: 'auto',
                              height: 'auto',
                              borderRadius: 1.5,
                            }}
                          />
                        </Box>
                        <ConfirmDialog
                          open={confirm.value}
                          onClose={confirm.onFalse}
                          title="Edit Resume"
                          content="Are you sure want to overwrite existing resume?"
                          action={
                            <Button
                              variant="contained"
                              color="warning"
                              onClick={() => {
                                setUploadClick(false);
                                handleUploadClick();
                                confirm.onFalse();
                              }}
                            >
                              Upload
                            </Button>
                          }
                        />
                      </>
                    ) : (
                      <RHFUploadBox
                        name="resumeUpload"
                        onDrop={handleDropResume}
                        acceptFile={{
                          'application/pdf': [],
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                            [],
                          'application/msword': [],
                          'text/plain': [],
                          'application/vnd.ms-powerpoint': [],
                          'application/vnd.openxmlformats-officedocument.presentationml.slideshow':
                            [],
                          'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                            [],
                        }}
                        onDelete={() => setValue('resumeUpload', null, { shouldValidate: true })}
                        minSize={1000} // give value in byte
                        maxSize={3000000}
                        placeholder={
                          <Box
                            rowGap={3}
                            columnGap={2}
                            display="flex"
                            alignItems="center"
                            sx={{ color: 'text.disabled' }}
                            gridTemplateColumns={{
                              xs: 'repeat(1, 1fr)',
                              sm: 'repeat(2, 1fr)',
                            }}
                          >
                            <Iconify
                              icon="eva:cloud-upload-fill"
                              style={{ width: '40px', height: '24px !important' }}
                            />

                            <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                              Upload Resume
                              <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                            </Typography>
                          </Box>
                        }
                        sx={{
                          py: 1,
                          width: 'auto',
                          height: 'auto',
                          borderRadius: 1.5,
                        }}
                      />
                    )}
                    <Box
                      rowGap={3}
                      columnGap={2}
                      display="flex"
                      alignItems="center"
                      gridTemplateColumns={{
                        xs: 'repeat(1, 1fr)',
                        sm: 'repeat(2, 1fr)',
                      }}
                    >
                      {editResume ? (
                        <Tooltip title="Download Resume" placement="top" arrow>
                          <Button
                            onClick={() => downloadResume(currentCand.resume)}
                            className="doNotDisable"
                          >
                            {getResumeIcon()}
                            <Typography ml={1} className="doNotDisable">
                              {currentCand.email}.{resumeTyp}
                            </Typography>
                          </Button>
                        </Tooltip>
                      ) : (
                        <>
                          {rFileNm ? getResumeIcon() : ''}
                          <Typography>{!rFileNm ? 'No file Uploaded' : rFileNm}</Typography>
                          {rFileNm && (
                            <Button variant="text" color="secondary" onClick={handleRemoveResume}>
                              <Iconify icon="entypo:cross" style={{ color: 'red' }} />
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                    <Stack spacing={1.5}>
                      <RHFAutocomplete
                        name="srcofhire"
                        placeholder="Source Of Hire"
                        label={
                          <div>
                            Source Of Hire
                            <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                          </div>
                        }
                        showPlusIcon
                        options={srcOfHrArr}
                        getOptionLabel={(option) => option.sourceName}
                        // value={selectedGender}
                        // onChange={(event, newValue) => {
                        //   setSelectedGender(newValue);
                        // }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.sourceName}>
                            {option.sourceName}
                          </li>
                        )}
                      />
                    </Stack>
                    <RHFTextField name="srcofhireNm" label="Source Name" />
                  </Box>
                </>
              )}

              {/* =============================== Personal Details============================ */}
              {activeStep === 1 && (
                <>
                  <Box>
                    <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
                      Personal Details:
                    </Typography>
                  </Box>
                  <Box
                    rowGap={3}
                    columnGap={2}
                    display="grid"
                    gridTemplateColumns={{
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    <RHFTextField
                      name="fname"
                      label={
                        <div>
                          First Name<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              <RHFAutocomplete
                                name="salutationName"
                                disableClearable
                                sx={{
                                  '& fieldset': { border: 'none' },
                                }}
                                options={nmTitleArr}
                                getOptionLabel={(option) => option.salutationName}
                                componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.nameTitleId}>
                                    {option.salutationName}
                                  </li>
                                )}
                              />
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <RHFTextField name="mname" label="Middle Name" />
                    <RHFTextField
                      name="lname"
                      label={
                        <div>
                          Last Name<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                    />
                    <RHFTextField
                      name="email"
                      label={
                        <div>
                          Email<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                    />
                    <RHFTextField
                      name="mobno"
                      label={
                        <div>
                          Mobile Number
                          <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={blockInvalidChar}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              <RHFAutocomplete
                                defaultValue={{
                                  phoneCode: '91', // Default symbol for Indian currency
                                  countryName: 'India', // Default country name
                                }}
                                name="phncode"
                                disableClearable
                                filterOptions={filterPhoneCode}
                                sx={{
                                  '& fieldset': { border: 'none' },
                                }}
                                options={countryArr}
                                getOptionLabel={(option) => option.phoneCode}
                                componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                                // value={selectedPhoneCode}
                                onChange={(event, value) => {
                                  setValue('phncode', value, { shouldValidate: true });
                                  trigger('mobno');
                                }}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.shortName}>
                                    {option.countryName}({option.phoneCode})
                                  </li>
                                )}
                              />
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Stack spacing={1.5}>
                      <RHFAutocomplete
                        name="gender"
                        placeholder="Gender"
                        label={
                          <div>
                            Gender<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                          </div>
                        }
                        options={genderArr}
                        getOptionLabel={(option) => option.genderName}
                        showPlusIcon
                        renderOption={(props, option) => (
                          <li {...props} key={option.genderName}>
                            {option.genderName}
                          </li>
                        )}
                      />
                    </Stack>
                    <Stack>
                      <RHFAutocomplete
                        name="cuurntloc"
                        placeholder="Current Location(City)"
                        disableClearable
                        ListboxComponent={ListboxComponent}
                        label={
                          <div>
                            Current Location(City)
                            <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                          </div>
                        }
                        options={cityArr}
                        getOptionLabel={(option) => option.cityName}
                        // value={selectedCurLoc}
                        // onChange={(event, newValue) => {
                        //   setSelectedCurLoc(newValue);
                        // }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.cityId}>
                            {option.cityName}
                          </li>
                        )}
                      />
                    </Stack>
                    <RHFAutocomplete
                      name="prefloc"
                      label="Preferred Location(City)"
                      placeholder="+ Preferred Location(City) "
                      disableClearable
                      multiple
                      ListboxComponent={ListboxComponent}
                      options={cityArr || []}
                      getOptionLabel={(option) => option.cityName || ''}
                      isOptionEqualToValue={(option, value) => option?.cityName === value?.cityName}
                      renderOption={(props, option) => (
                        <li {...props} key={option?.cityId}>
                          {option?.cityName}
                        </li>
                      )}
                      renderTags={(selected, getTagProps) =>
                        selected
                          .filter((option) => option && option?.cityId) // Filter out null/invalid entries
                          .map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option?.cityId}
                              label={option?.cityName}
                              size="small"
                              color="info"
                              variant="soft"
                            />
                          ))
                      }
                    />
                    <Stack spacing={1.5}>
                      <RHFAutocomplete
                        name="uniqueNum"
                        placeholder="Unique Identity Document"
                        // label={
                        //   <div>
                        //     Unique Identity Document
                        //   </div>
                        // }
                        label="Unique Identity Document"
                        showPlusIcon
                        disableClearable
                        options={uniqueNum}
                        getOptionLabel={(option) => option}
                        onChange={(event, value) => {
                          setValue('uniqueNum', value, { shouldValidate: true });
                          setUniqueNumber(value);
                          trigger('uniqueno'); // Important for dynamic validation to fire
                        }}
                        // value={selectedCurLoc}
                        // onChange={(event, newValue) => {
                        //   setSelectedCurLoc(newValue);
                        // }}
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            {option}
                          </li>
                        )}
                      />
                    </Stack>
                    {uniqueNumber === 'PAN' && (
                      <Stack spacing={1}>
                        <RHFTextField
                          name="uniqueno"
                          label={
                            <div>
                              Pan Card Number
                              <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                            </div>
                          }
                        />
                      </Stack>
                    )}
                    {uniqueNumber === 'AADHAR' && (
                      <Stack spacing={1}>
                        <RHFTextField
                          name="uniqueno"
                          label={
                            <div>
                              Aadhar Card Number
                              <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                            </div>
                          }
                          type="number"
                          onKeyDown={blockInvalidChar}
                        />
                      </Stack>
                    )}
                    <Stack spacing={1}>
                      <RHFTextField name="linkedinUrl" label="LinkedIn Profile Url" />
                    </Stack>
                    <Stack spacing={1}>
                      <Box
                        rowGap={2}
                        columnGap={2}
                        display="flex"
                        gridTemplateColumns={{
                          xs: 'repeat(1, 1fr)',
                          sm: 'repeat(2, 1fr)',
                        }}
                      >
                        <Typography variant="subtitle2" mt={1}>
                          Ready to Relocate:
                        </Typography>
                        <RHFRadioGroup row spacing={2} name="readytolocate" options={yesNoArr} />
                      </Box>
                    </Stack>
                  </Box>
                </>
              )}

              {/* ------------------------------------------------- Professional Details ----------------------------------------------------*/}
              {activeStep === 2 && (
                <>
                  <Box>
                    <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
                      Professional Details:
                    </Typography>
                  </Box>
                  <Box
                    rowGap={3}
                    columnGap={2}
                    display="grid"
                    gridTemplateColumns={{
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    <Stack sx={{ textAlign: 'right' }}>
                      <RHFTextField
                        name="expinyrs"
                        type="number"
                        placeholder="0.0"
                        label="Experience in Years"
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={blockInvalidChar}
                      />
                    </Stack>
                    <RHFTextField
                      name="relexp"
                      type="number"
                      placeholder="0.0"
                      label="Relevant Experience In Year"
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={blockInvalidChar}
                    />
                    <Stack spacing={1.5}>
                      <RHFAutocomplete
                        name="candQal"
                        placeholder="Highest Qualification"
                        label={
                          <div>
                            Qualification
                            <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                          </div>
                        }
                        options={qualArr}
                        getOptionLabel={(option) => option.qualificationTitle}
                        showPlusIcon
                        // value={selectedQual}
                        // onChange={(event, newValue) => {
                        //   setSelectedQual(newValue);
                        // }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.qualificationTitle}>
                            {option.qualificationTitle}
                          </li>
                        )}
                      />
                    </Stack>
                    <Stack spacing={1.5}>
                      <RHFAutocomplete
                        name="currentjob"
                        placeholder="Current Job Designation / Role"
                        label="Designation"
                        options={desgntnArr}
                        getOptionLabel={(option) => option.designationName}
                        showPlusIcon
                        // value={selectedDesgnt}
                        // onChange={(event, newValue) => {
                        //   setSelectedDesgnt(newValue);
                        // }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.designationName}>
                            {option.designationName}
                          </li>
                        )}
                      />
                    </Stack>
                    <RHFTextField name="currentcomp" label="Current Employer" sx={{ mt: -1.5 }} />
                    <RHFTextField
                      name="noticeprd"
                      placeholder="Notice Period in Days"
                      type="number"
                      label="Notice Period"
                      onKeyDown={blockInvalidChar}
                      sx={{ mt: -1.5 }}
                    />
                    <RHFTextField
                      name="currentsal"
                      label="Current Salary"
                      placeholder="0.00"
                      type="number"
                      onKeyDown={blockInvalidChar}
                      onWheel={(e) => e.target.blur()}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              <RHFAutocomplete
                                name="currentsalcurncy"
                                defaultValue={{
                                  currencySymbol: '₹', // Default symbol for Indian currency
                                  countryName: 'India', // Default country name
                                }}
                                filterOptions={filterCurrency}
                                disableClearable
                                sx={{
                                  '& fieldset': { border: 'none' },
                                }}
                                options={countryArr}
                                getOptionLabel={(option) => option.currencySymbol}
                                componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                                // value={selectedCurSalCer}
                                onChange={(event, value) => {
                                  setValue('currentsalcurncy', value, { shouldValidate: true });
                                  trigger('currentsal');
                                }}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.shortName}>
                                    {option.countryName}({option.currencySymbol})
                                  </li>
                                )}
                              />
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <RHFTextField
                      name="exptsal"
                      label="Expected Salary"
                      placeholder="0.00"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={blockInvalidChar}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              <RHFAutocomplete
                                name="exptsalcurncy"
                                defaultValue={{
                                  currencySymbol: '₹', // Default symbol for Indian currency
                                  countryName: 'India', // Default country name
                                }}
                                filterOptions={filterCurrency}
                                disableClearable
                                sx={{
                                  '& fieldset': { border: 'none' },
                                }}
                                options={countryArr}
                                getOptionLabel={(option) => option.currencySymbol}
                                componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                                // value={selectedExpSalCer}
                                onChange={(event, value) => {
                                  setValue('exptsalcurncy', value, { shouldValidate: true });
                                  trigger('exptsal');
                                }}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.shortName}>
                                    {option.countryName}({option.currencySymbol})
                                  </li>
                                )}
                              />
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <RHFAutocomplete
                      name="primaryskl"
                      label={
                        <div>
                          Primary Skills
                          <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                      placeholder="+ Primary Skills"
                      multiple
                      options={skillArr}
                      getOptionLabel={(option) => option.skillName}
                      showPlusIcon
                      prerequisiteMasters={['Skill Category']}
                      isOptionEqualToValue={(option, value) => option.skillName === value.skillName}
                      onChange={(event, value) => {
                        setPriSkillArr(value);
                        setValue('primaryskl', value, { shouldValidate: true });
                        // handleSecSkill();
                        const filteredSecSkills = secSkillArr.filter(
                          (secSkill) =>
                            // Filter out options that are already selected as primary skills
                            !value.some((priSkill) => priSkill.skillName === secSkill.skillName)
                        );

                        // Update the secondary skills field
                        setValue('secondskl', filteredSecSkills, { shouldValidate: true });
                        setSecSkillArr(filteredSecSkills);
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.skillId}>
                          {`${option.skillName}`}
                        </li>
                      )}
                      renderTags={(selected, getTagProps) =>
                        selected.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.skillId}
                            label={option.skillName}
                            size="small"
                            color="info"
                            variant="soft"
                          />
                        ))
                      }
                    />
                    <RHFAutocomplete
                      name="secondskl"
                      label="Secondary Skills"
                      placeholder="+ Secondary Skills"
                      multiple
                      showPlusIcon
                      prerequisiteMasters={['Skill Category']}
                      // options={skillArr}
                      options={skillArr.filter(
                        (option) =>
                          // Filter out options that are already selected as primary skills
                          !priSkillArr.some(
                            (selectedSkill) => selectedSkill.skillName === option.skillName
                          )
                      )}
                      getOptionLabel={(option) => option.skillName}
                      isOptionEqualToValue={(option, value) => option.skillName === value.skillName}
                      onChange={(event, value) => {
                        setSecSkillArr(value);
                        setValue('secondskl', value, { shouldValidate: true });
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.skillId}>
                          {`${option.skillName}`}
                        </li>
                      )}
                      renderTags={(selected, getTagProps) =>
                        selected.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.skillId}
                            label={option.skillName}
                            size="small"
                            color="info"
                            variant="soft"
                          />
                        ))
                      }
                    />

                    <Stack spacing={1}>
                      <Box
                        rowGap={2}
                        columnGap={2}
                        display="flex"
                        gridTemplateColumns={{
                          xs: 'repeat(1, 1fr)',
                          sm: 'repeat(2, 1fr)',
                        }}
                      >
                        <Typography variant="subtitle2" mt={1}>
                          Is Currently Working With This Employer?:
                        </Typography>
                        <RHFRadioGroup
                          row
                          spacing={2}
                          name="iscurrentemp"
                          options={yesNoArr}
                          onChange={(event, value) => {
                            setValue('iscurrentemp', value, { shouldValidate: true });
                            if (value === 'false') {
                              setValue('hasresigned', 'true', { shouldValidate: true });
                            }
                          }}
                        />
                      </Box>
                    </Stack>
                    <Stack spacing={1} marginLeft={1}>
                      <Box
                        rowGap={2}
                        columnGap={2}
                        display="flex"
                        gridTemplateColumns={{
                          xs: 'repeat(1, 1fr)',
                          sm: 'repeat(2, 1fr)',
                        }}
                      >
                        <Typography variant="subtitle2" mt={1}>
                          Has Resigned:
                        </Typography>
                        <RHFRadioGroup
                          row
                          spacing={2}
                          name="hasresigned"
                          options={yesNoArr}
                          disabled={watch('iscurrentemp') === 'false'}
                        />
                      </Box>
                    </Stack>
                    {(watch('hasresigned') === true || watch('hasresigned') === 'true') && (
                      <Controller
                        name="resigneddate"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <DatePicker
                            label="Resignation Date"
                            value={field.value}
                            onChange={(newValue) => {
                              field.onChange(newValue);
                            }}
                           
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!error,
                                helperText: error?.message,
                              },
                            }}
                          />
                        )}
                      />
                    )}

                    <Controller
                      name="canjoinby"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <DatePicker
                          label="Can Join By"
                          value={field.value}
                          onChange={(newValue) => {
                            field.onChange(newValue);
                          }}
                         
                          minDate={new Date()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!error,
                              helperText: error?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </>
              )}
              {/* ----------------------------- Education Details ---------------------------------------------------------------- */}
              {activeStep === 3 && (
                <CandNewEditEduCompo
                  universityData={universityArr}
                  qualData={qualArr}
                  qualTypeData={qualTypeArr}
                  streamData={streamArr}
                  candidateData={currentCand}
                  candidateEduData={currentCandEduDtl}
                />
              )}
              {/* ----------------------------- Certificate Details ---------------------------------------------------------------- */}
              {activeStep === 4 && (
                <CandNewEditCertiCompo certNmArr={certNmArr} certInstNmArr={certInstNmArr} />
              )}
              {/* ----------------------------- Experience Details ---------------------------------------------------------------- */}
              {/* Experience Section */}
              {activeStep === 5 && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={switchState.experience}
                        onChange={(event) => handleSwitchChange(event, 'experience')}
                        name="expSwitch"
                        color="primary"
                      />
                    }
                    label="Have an Experience ?"
                  />

                  {/* Render CandNewEditExpCompo if the experience switch is on */}
                  {switchState.experience && (
                    <CandNewEditExpCompo
                      desgData={desgntnArr}
                      cntryData={countryArr}
                      filterCurrency={filterCurrency}
                      switchState={switchState}
                    />
                  )}
                </>
              )}

              {/* ----------------------------- Reference Details ---------------------------------------------------------------- */}

              {activeStep === steps.length - 1 ? (
                // Final Step Buttons
                <>
                  <CandNewEditRefCompo desgRefData={desgntnArr} />

                  {!currentCandView && (
                    <Stack justifyContent="flex-end" direction="row" spacing={2} sx={{ mt: 3 }}>
                      {/* Back Button */}
                      {!disabled && activeStep > 0 && (
                        <Button
                          variant="soft"
                          onClick={handleBack}
                          sx={{
                            color: '#00838f',
                            bgcolor: '#CAFDF5',
                            '&:hover': {
                              bgcolor: '#CAFDF5',
                            },
                          }}
                        >
                          Previous
                        </Button>
                      )}

                      {/* Cancel Button */}
                      {!disabled && (
                        <Button
                          variant="soft"
                          color="info"
                          startIcon={<Iconify icon="carbon:view" />}
                          onClick={() =>
                            router.push(paths.dashboard.talentAcquisition.master.ListCandidate)
                          }
                        >
                          Back To List
                        </Button>
                      )}

                      {/* Save Changes Button */}

                      {/* {!disabled && (
                        <LoadingButton
                          type="submit"
                          variant="soft"
                          color="success"
                          loading={loadingSend.value}
                          onClick={handleSaveNext}
                        >
                          {!currentCand ? 'Create Candidate' : 'Save & Next'}
                        </LoadingButton>
                      )} */}
                      {!disabled && (
                        <LoadingButton
                          type="button"
                          variant="soft"
                          color="success"
                          loading={loadingSend.value}
                          onClick={
                            activeStep === steps.length - 1
                              ? () => handleFormSubmission(methods.getValues(), false) // Final save
                              : handleSaveNext // Intermediate step save
                          }
                        >
                          {activeStep === steps.length - 1
                            ? !currentCand
                              ? 'Create Candidate'
                              : 'Save Changes'
                            : 'Save & Next'}
                        </LoadingButton>
                      )}
                    </Stack>
                  )}
                </>
              ) : (
                // Steps Before Final Step
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 3,
                  }}
                >
                  {/* Back Button */}
                  {!disabled && activeStep > 0 && (
                    <Button
                      variant="soft"
                      onClick={handleBack}
                      sx={{
                        mr: 2, // Adds margin to the right
                        color: '#00838f',
                        bgcolor: '#CAFDF5',
                        '&:hover': {
                          bgcolor: '#CAFDF5',
                        },
                      }}
                    >
                      Previous
                    </Button>
                  )}
                  {/* Spacer to push other buttons to the right */}
                  <Box sx={{ flexGrow: 1 }} />

                  {!disabled && (activeStep >= 1 || currentCand) && (
                    <LoadingButton
                      type="button"
                      onClick={
                        activeStep === steps.length - 1
                          ? () => handleFormSubmission(methods.getValues(), false) // Final step submission
                          : handleSaveNext // Move to next step
                      }
                      variant="soft"
                      color="primary"
                      loading={loadingSend.value}
                      sx={{
                        mr: 2,
                        bgcolor: 'primary',
                        '&:hover': {
                          bgcolor: 'primary',
                        },
                      }}
                    >
                      {activeStep === steps.length - 1
                        ? !currentCand
                          ? 'Create Candidate'
                          : 'Save Changes'
                        : 'Save & Next'}
                    </LoadingButton>
                  )}

                  {/* Next Button */}
                  {!disabled && (
                    <Button
                      variant="soft"
                      onClick={handleNext}
                      sx={{
                        color: '#026544',
                        bgcolor: '#77ed8b7a', // Optional, for consistent theming
                        '&:hover': {
                          bgcolor: 'success.light', // Optional hover effect
                        },
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </FormProvider>
        <>
          {/* Only render the navigation buttons if "disabled" is true */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end', // Align to the right
              gap: 2,
              mt: 1,
            }}
          >
            {/* Back Button */}
            {disabled && activeStep >= 1 && (
              <Button
                variant="soft"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{
                  color: '#00838f',
                  bgcolor: '#CAFDF5',
                  '&:hover': {
                    bgcolor: '#CAFDF5',
                  },
                }}
              >
                Previous
              </Button>
            )}

            {/* Next Button */}
            {disabled && activeStep !== steps.length - 1 && (
              <Button
                variant="soft"
                onClick={handleNext}
                sx={{
                  color: '#026544',
                  bgcolor: '#77ed8b7a',
                  '&:hover': {
                    bgcolor: 'success.light',
                  },
                }}
              >
                Next
              </Button>
            )}
          </Box>
        </>
      </Card>
      {/* ------------------------------------------------ Autofill resume Section --------------------------------------------------------*/}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={quickAutoFill.value}
        onClose={quickAutoFill.onFalse}
      >
        <DialogTitle>Autofill</DialogTitle>
        <DialogContent>
          <Typography variant="body1">How do you want to process the resume?</Typography>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            onClick={() => {
              quickAutoFill.onFalse();
              handleAutoFillResume();
            }}
          >
            Autofill with Resume
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={() => {
              quickAutoFill.onFalse();
            }}
          >
            Fill Form Manually
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
CandidateNewEditForm.propTypes = {
  currentCand: PropTypes.object,
  currentCandPrefLoc: PropTypes.array,
  currentCandEduDtl: PropTypes.array,
  currentCandSkils: PropTypes.array,
  currentCandCertDtl: PropTypes.array,
  currentCandExpDtl: PropTypes.array,
  currentCandRefDtl: PropTypes.array,
  currentCandView: PropTypes.bool,
  disabled: PropTypes.bool,
};
// sprint 9 code changed with candidate form. with changes save&next feature , default field  added and UiqueNUm Validation Removed .
